// orthanc-api.ts
// Complete Orthanc REST API wrapper (proprietary API, not DICOMweb)
// DICOMweb (QIDO-RS, WADO-RS, STOW-RS) is handled separately by dicom-web-api.ts
// ref: https://orthanc.uclouvain.be/book/users/rest-cheatsheet.html
//
// Orthanc ID: Internal short-hash identifier (e.g. "dc65762c-f476e8b9-898f657d-...")
//   used to address all resources. NOT the same as DICOM UIDs.
//   Use POST /tools/lookup to resolve a DICOM UID to an Orthanc ID.
//
// MainDicomTags: A configurable subset of DICOM tags that Orthanc extracts at
//   import time and stores in its database index. Controlled by the server-side
//   "ExtraMainDicomTags" configuration. Only these tags appear in summary resource
//   responses (GET /studies/{id}, GET /series/{id}, etc.).
//   For full DICOM tags, use /instances/{id}/tags or /instances/{id}/simplified-tags.

import type { DicomArchiveConfig } from '@/src/supabase';

import z from 'zod';

import { orthancJwt } from './useOrthancAuth';

// ============ Type Definitions ============
// Orthanc resource types model the native REST API responses.
// "ID" is Orthanc's internal short hash — not the DICOM UID.
// "MainDicomTags" contains only the tags configured in Orthanc's ExtraMainDicomTags;
// use /tags or /simplified-tags sub-routes for the full DICOM tag set.

// Response from GET /patients/{id}
// Orthanc's top-level grouping of studies by PatientID
export interface OrthancPatientInfo {
  // Orthanc internal short-hash ID (not DICOM PatientID)
  ID: string;
  // True once no new instances have been received for this patient recently
  IsStable: boolean;
  // ISO 8601 timestamp of the last change to this patient
  LastUpdate: string;
  // Subset of DICOM Patient Module tags configured in ExtraMainDicomTags
  MainDicomTags: {
    PatientID?: string;
    PatientName?: string;
    PatientBirthDate?: string;
    PatientSex?: string;
    PatientWeight?: string;
    PatientSize?: string;
    [key: string]: any;
  };
  // Orthanc IDs of child studies belonging to this patient
  Studies: string[];
  Type: 'Patient';
}

// Response from GET /studies/{id}
export interface OrthancStudyInfo {
  ID: string;
  IsStable: boolean;
  LastUpdate: string;
  Metadata?: Record<string, string>;
  MainDicomTags: {
    StudyInstanceUID: string;
    [key: string]: string | undefined;
  };
  ParentPatient: string;
  PatientMainDicomTags: {
    [key: string]: string | undefined;
  };
  Series: string[];
  Type: 'Study';
}

export interface OrthancSeriesInfo {
  ID: string;
  IsStable: boolean;
  LastUpdate: string;
  MainDicomTags: {
    SeriesInstanceUID: string;
    [key: string]: string | undefined;
  };
  ParentStudy: string;
  Instances: string[];
  Type: 'Series';
}

export interface OrthancInstanceInfo {
  ID: string;
  MainDicomTags: {
    SOPInstanceUID: string;
    [key: string]: string | undefined;
  };
  ParentSeries: string;
  Type: 'Instance';
  FileSize?: number;
  FileUuid?: string;
}

export interface Ocg19PerFrameInfo {
  // MREchoSequence
  EchoNumbers: string;
  EffectiveEchoTime: string;
  // CardiacSynchronizationSequence
  NominalCardiacTriggerDelayTime: string;
  NominalPercentageOfCardiacPhase: string;
  RRIntervalTimeNominal: string;
  // MRImageFrameTypeSequence
  AcquisitionContrast: string;
  ComplexImageComponent: string;
  FrameType: string;
  PixelPresentation: string;
  VolumeBasedCalculationTechnique: string;
  VolumetricProperties: string;
  // FrameContentSequence
  FrameReferenceDateTime: string;
  FrameAcquisitionDuration: string;
  StackID: string;
  InStackPositionNumber: string;
  TemporalPositionIndex: string;
  FrameAcquisitionNumber: string;
  DimensionIndexValues: string;
  // PlanePositionSequence
  ImagePositionPatient: string;
  // PlaneOrientationSequence
  ImageOrientationPatient: string;
  // PixelMeasuresSequence
  SliceThickness: string;
  PixelSpacing: string;
  // FrameVOILUTSequence
  WindowCenter: string;
  WindowWidth: string;
  // PixelValueTransformationSequence
  RescaleIntercept: string;
  RescaleSlope: string;
  RescaleType: string;
}

export interface Ocg19InstanceInfo {
  // (0008,xxxx) — Identity, classification, timing
  SpecificCharacterSet: string;
  ImageType: string;
  InstanceCreationDate: string;
  InstanceCreationTime: string;
  SOPClassUID: string;
  SOPInstanceUID: string;
  AcquisitionDate: string;
  AcquisitionTime: string;
  ContentDate: string;
  ContentTime: string;
  AcquisitionDateTime: string;
  Modality: string;
  SeriesDescription: string;
  PixelPresentation: string;
  // (0018,xxxx) — Acquisition parameters
  ScanOptions: string;
  SequenceName: string;
  SliceThickness: string;
  RepetitionTime: string;
  EchoTime: string;
  InversionTime: string;
  ProtocolName: string;
  TriggerTime: string;
  NominalInterval: string;
  HeartRate: string;
  CardiacRRIntervalSpecified: string;
  CardiacSignalSource: string;
  CardiacNumberOfImages: string;
  FlipAngle: string;
  // (0020,xxxx) — Spatial & instance identification
  SeriesInstanceUID: string;
  AcquisitionNumber: string;
  InstanceNumber: string;
  ImagePositionPatient: string;
  ImageOrientationPatient: string;
  FrameOfReferenceUID: string;
  TemporalPositionIdentifier: string;
  SliceLocation: string;
  ImageComments: string;
  TemporalPositionIndex: string;
  // (0028,xxxx) — Image pixel & display
  SamplesPerPixel: string;
  PhotometricInterpretation: string;
  PlanarConfiguration: string;
  NumberOfFrames: string;
  Rows: string;
  Columns: string;
  PixelSpacing: string;
  BitsAllocated: string;
  BitsStored: string;
  PixelRepresentation: string;
  WindowCenter: string;
  WindowWidth: string;
  RescaleIntercept: string;
  RescaleSlope: string;
  RescaleType: string;
  RedPaletteColorLookupTableDescriptor: string;
  GreenPaletteColorLookupTableDescriptor: string;
  BluePaletteColorLookupTableDescriptor: string;
  TransferSyntaxUID: string;
  OrthancInstanceId: string;
  PerFrame: Ocg19PerFrameInfo[];
  /*   'GEMS_ACQU_01': {
      'VelocityEncoding':      '0019,10cc',
      'VelocityEncodeScale':   '0019,10e2',
    },
    'GEMS_PARM_01': {
        'VasCollapseFlag':       '0043,1030',
    },
    'Philips Imaging DD 001': {
        'PCVelocity':            '2001,101a',
    },
    'Philips MR Imaging DD 005': {
        'OriginalRescaleIntercept': '2005,1409',
        'OriginalRescaleSlope':     '2005,140a',
        'OriginalRescaleType':      '2005,140b',
    },
    'SIEMENS MR SDS 01': {
        'VelocityDescription':   '0021,1129',
    }, */
  Private: {
    PhaseContrastFlowAxis: string;
    VelocityEncoding: string;
    VelocityEncodeScale: string;
    VasCollapseFlag: string;
    VasFlags: string;
    IsMagnitudeMasked: boolean;

    PCVelocity: string;
    OriginalRescaleIntercept: string;
    OriginalRescaleSlope: string;
    OriginalRescaleType: string;

    VelocityDescription: string;
  };
  Vendors: string[];
}

// Response from GET /statistics or GET /{level}/{id}/statistics
export interface OrthancStatistics {
  CountInstances: number;
  CountPatients: number;
  CountSeries: number;
  CountStudies: number;
  // Total size on disk as human-readable string (e.g. "1.2GB")
  TotalDiskSize: string;
  TotalDiskSizeMB: number;
  // Total size before compression as human-readable string
  TotalUncompressedSize: string;
  TotalUncompressedSizeMB: number;
}

// Response from GET /{level}/{id}/metadata?expand
// Without ?expand, metadata endpoints return string[] (key names only)
export interface OrthancMetadata {
  [key: string]: string;
}

// Single entry in the changes log returned by GET /changes
export interface OrthancChangeEntry {
  // Type of change: "StableStudy", "NewInstance", "NewSeries", etc.
  ChangeType: string;
  // ISO 8601 timestamp
  Date: string;
  // Orthanc ID of the affected resource
  ID: string;
  // REST path to the affected resource (e.g. "/instances/abc123")
  Path: string;
  // "Patient", "Study", "Series", or "Instance"
  ResourceType: string;
  // Monotonically increasing sequence number (use as "since" for pagination)
  Seq: number;
}

// Response from GET /changes
export interface OrthancChanges {
  Changes: OrthancChangeEntry[];
  // True if there are no more changes after this batch
  Done: boolean;
  // Sequence number of the last change in this batch (pass as "since" for next page)
  Last: number;
}

// Single entry in the exports log returned by GET /exports
export interface OrthancExportEntry {
  // ISO 8601 timestamp
  Date: string;
  // Orthanc ID of the exported resource
  ID: string;
  // REST path to the exported resource
  Path: string;
  // Symbolic name of the target modality or peer
  RemoteModality: string;
  // "Patient", "Study", "Series", or "Instance"
  ResourceType: string;
  // Monotonically increasing sequence number
  Seq: number;
}

// Response from GET /exports
export interface OrthancExports {
  Exports: OrthancExportEntry[];
  // True if there are no more exports after this batch
  Done: boolean;
  // Sequence number of the last export in this batch
  Last: number;
}

// Response from GET /jobs/{id}
export interface OrthancJobInfo {
  // ISO 8601 timestamp when the job completed (absent if still running)
  CompletionTime?: string;
  // Job-type-specific payload (varies by Type)
  Content: Record<string, any>;
  // ISO 8601 timestamp when the job was created
  CreationTime: string;
  // Wall-clock runtime in seconds (absent if not completed)
  EffectiveRuntime?: number;
  // Orthanc error code (0 = success, absent if not failed)
  ErrorCode?: number;
  // Human-readable error description
  ErrorDescription?: string;
  // Job identifier
  ID: string;
  // Job priority (0-255, higher = more urgent)
  Priority: number;
  // Completion percentage (0-100)
  Progress: number;
  State: 'Pending' | 'Running' | 'Success' | 'Failure' | 'Paused' | 'Retry';
  // ISO 8601 timestamp of the last state change
  Timestamp: string;
  // Job type identifier (e.g. "Archive", "DicomModalityStore", etc.)
  Type: string;
}

// Response from GET /system
export interface OrthancSystemInfo {
  // REST API version number
  ApiVersion: number;
  // Name of the database backend plugin (null if using built-in SQLite)
  DatabaseBackendPlugin: string | null;
  // Database schema version
  DatabaseVersion: number;
  // Configured DICOM Application Entity Title
  DicomAet: string;
  // Configured DICOM port for incoming associations
  DicomPort: number;
  // Whether the labels feature is enabled in the database
  HasLabels: boolean;
  // Configured HTTP port for the REST API
  HttpPort: number;
  // Whether the HTTP server requires authentication
  IsHttpServerSecure: boolean;
  // Configured server name
  Name: string;
  // Whether plugins are enabled
  PluginsEnabled: boolean;
  // Name of the storage area plugin (null if using built-in filesystem)
  StorageAreaPlugin: string | null;
  // Orthanc version string (e.g. "1.12.3")
  Version: string;
}

// Response from GET /{level}/{id}/attachments/{name}/info
export interface OrthancAttachmentInfo {
  // MD5 hash of the on-disk (possibly compressed) data
  CompressedMD5: string;
  // Size in bytes on disk
  CompressedSize: number;
  // "StoredCompression" type (e.g. "None", "ZLib")
  CompressionType: string;
  // Numeric content type identifier (0 = DICOM file, 1 = DICOM-as-JSON, etc.)
  ContentType: number;
  // MD5 hash of the uncompressed original data
  UncompressedMD5: string;
  // Size in bytes of the uncompressed original data
  UncompressedSize: number;
  // Internal storage UUID
  Uuid: string;
}

// Body for POST /tools/find
export interface OrthancFindRequest {
  // DICOM hierarchy level to search
  Level: 'Patient' | 'Study' | 'Series' | 'Instance';
  // Tag filters (keys are DICOM tag names or hex codes, values are match patterns)
  Query: Record<string, string>;
  // If true, return full resource objects instead of just Orthanc IDs
  Expand?: boolean;
  // Maximum number of results to return
  Limit?: number;
  // Number of results to skip (for pagination)
  Since?: number;
  // Filter by labels attached to resources
  Labels?: string[];
  // How to combine multiple labels: require All, Any, or None to match
  LabelsConstraint?: 'All' | 'Any' | 'None';
  // Additional tags to include in the response beyond MainDicomTags
  RequestedTags?: string[];
  // If true, return tags in short format { "0008,0018": "1.2.3..." }
  Short?: boolean;
  // If true, return tags in full DICOM JSON format with Name/Type/Value
  Full?: boolean;
  // Defines response content per resource (new in 1.12.5, overrides Expand)
  // Allowed: "MainDicomTags", "Metadata", "Children", "Parent", "Labels", "Status", "IsStable", "IsProtected", "Attachments"
  // Semicolon-separated, e.g. "MainDicomTags;Children"
  ResponseContent?: string;
}

// Configuration for a DICOM modality peer
// Used with PUT /modalities/{id} and returned by GET /modalities/{id}/configuration
export interface OrthancModalityConfig {
  // Application Entity Title
  AET: string;
  // Hostname or IP address
  Host: string;
  // DICOM port number
  Port: number;
  // Manufacturer string for workarounds (e.g. "GenericNoWildcardInDates")
  Manufacturer?: string;
  // Allow C-ECHO operations to this modality
  AllowEcho?: boolean;
  // Allow C-FIND operations to this modality
  AllowFind?: boolean;
  // Allow C-FIND worklist operations to this modality
  AllowFindWorklist?: boolean;
  // Allow C-GET operations from this modality
  AllowGet?: boolean;
  // Allow C-MOVE operations from this modality
  AllowMove?: boolean;
  // Allow C-STORE operations to this modality
  AllowStore?: boolean;
  // Allow transcoding when sending to this modality
  AllowTranscoding?: boolean;
}

// Body for POST /{level}/{id}/anonymize
export interface OrthancAnonymizeRequest {
  // Tags to replace with specified values (key = tag name or hex, value = new value)
  Replace?: Record<string, string>;
  // Tags to preserve unchanged during anonymization
  Keep?: string[];
  // Tags to explicitly remove
  Remove?: string[];
  // Allow modifying protected tags (PatientID, StudyInstanceUID, etc.)
  Force?: boolean;
  // DICOM standard version for the anonymization profile (e.g. "2021b")
  DicomVersion?: string;
  // If true, preserve private (odd-group) tags
  KeepPrivateTags?: boolean;
  // If true, keep the original resource; if false, replace it in-place
  KeepSource?: boolean;
  // Private creator string when replacing private tags
  PrivateCreator?: string;
  // If true, silently ignore errors instead of aborting
  Permissive?: boolean;
  // If true, run as a background job and return OrthancJobInfo immediately
  Asynchronous?: boolean;
  // Job priority (0-255) when Asynchronous is true
  Priority?: number;
  // Target transfer syntax UID to transcode the output
  Transcode?: string;
  // Keep the labels of all resource levels (defaults to false)
  KeepLabels?: boolean;
  // Quality for lossy transcoding (1-100), used when Transcode targets a lossy syntax
  LossyQuality?: number;
}

// Body for POST /{level}/{id}/modify
export interface OrthancModifyRequest {
  // Tags to replace with specified values
  Replace?: Record<string, string>;
  // Tags to explicitly remove
  Remove?: string[];
  // Tags to preserve unchanged
  Keep?: string[];
  // Allow modifying protected tags
  Force?: boolean;
  // If true, remove all private (odd-group) tags
  RemovePrivateTags?: boolean;
  // If true, keep the original resource; if false, replace it in-place
  KeepSource?: boolean;
  // Private creator string when replacing private tags
  PrivateCreator?: string;
  // If true, silently ignore errors instead of aborting
  Permissive?: boolean;
  // If true, run as a background job and return OrthancJobInfo immediately
  Asynchronous?: boolean;
  // Job priority (0-255) when Asynchronous is true
  Priority?: number;
  // Target transfer syntax UID to transcode the output
  Transcode?: string;
  // Keep the labels of all resource levels (defaults to false)
  KeepLabels?: boolean;
}

// Query params for GET /{level}/{id}/archive and GET /{level}/{id}/media
export interface OrthancArchiveGetOptions {
  // Target transfer syntax UID to transcode all instances in the archive
  transcode?: string;
  // Suggested filename for the Content-Disposition header
  filename?: string;
}

// Body for POST /{level}/{id}/archive and POST /{level}/{id}/media
export interface OrthancArchivePostOptions {
  // If true, run as a background job and return OrthancJobInfo immediately
  Asynchronous?: boolean;
  // Job priority (0-255) when Asynchronous is true
  Priority?: number;
  // Target transfer syntax UID to transcode all instances
  Transcode?: string;
}

// Pagination for list endpoints (GET /patients, /studies, /series, /instances)
// Not in the cheatsheet table — documented in Orthanc REST API general usage
export interface OrthancListOptions {
  since?: number;
  limit?: number;
  // If true, report DICOM tags in full format
  full?: boolean;
  // If true, report DICOM tags in hex short format
  short?: boolean;
  // Semicolon-separated extra DICOM tags to include
  requestedTags?: string;
  // Response content definition (new in 1.12.5, overrides expand)
  responseContent?: string;
}

// Format options for endpoints returning DICOM tags
// Applies to: /tags, /shared-tags, /module, /module-patient, /header, /instances-tags
export interface OrthancTagFormatOptions {
  // If true, return tags in short format { "0008,0018": "1.2.3..." }
  short?: boolean;
  // If true, return tags with human-readable key names { "SOPInstanceUID": "1.2.3..." }
  simplify?: boolean;
  // Tags whose values should not be truncated (comma-separated tag names or hex codes)
  ignoreLength?: string;
}

export const FullTagSchema = z.intersection(z.object({
  Name: z.string(),
}), z.union([
  z.object({
    Type: z.literal('String'),
    Value: z.string(),
  }),
  z.object({
    Type: z.literal('Null'),
    Value: z.literal(null),
  }),
]));
export type FullTag = z.infer<typeof FullTagSchema>;

export const FullTagDatasetSchema = z.record(z.string(), FullTagSchema);
export type FullTagDataset = z.infer<typeof FullTagDatasetSchema>;

export const SimpleTagSchema = z.union([z.string(), z.null()]);
export type SimpleTag = z.infer<typeof SimpleTagSchema>;

export const SimpleTagDatasetSchema = z.record(z.string(), SimpleTagSchema);
export type SimpleTagDataset = z.infer<typeof SimpleTagDatasetSchema>;

type TagFormatResult<T extends OrthancTagFormatOptions | undefined>
  = undefined extends T
    ? FullTagDataset
    : T extends { short: true } | { simplify: true }
      ? SimpleTagDataset
      : T extends { short: false | undefined; simplify: false | undefined }
        ? FullTagDataset
        : SimpleTagDataset | FullTagDataset;

// Options for image rendering endpoints
// Applies to: /rendered, /preview, /frames/{f}/rendered, /frames/{f}/preview
export interface OrthancRenderedOptions {
  // JPEG quality (1-100), only when Accept is image/jpeg
  quality?: number;
  // Target width in pixels (aspect ratio preserved if only one dimension given)
  width?: number;
  // Target height in pixels
  height?: number;
  // Use smooth interpolation when resizing
  smooth?: boolean;
  // Override window center for VOI LUT
  windowCenter?: number;
  // Override window width for VOI LUT
  windowWidth?: number;
  // If true, return placeholder PNG instead of 415 error
  returnUnsupportedImage?: boolean;
  // Output format: "image/png" (default), "image/jpeg", "image/x-portable-arbitrarymap"
  accept?: string;
}

// Body for POST /tools/create-dicom
export interface OrthancCreateDicomRequest {
  // DICOM tags as key-value pairs (keys can be hex "0010,0020" or names "PatientID")
  Tags: Record<string, string>;
  // Base64-encoded pixel data or PDF content to embed
  Content?: string;
  // Orthanc ID of the parent resource (study or series) to attach the new instance to
  Parent?: string;
  // If true, interpret binary string values as Base64
  InterpretBinaryTags?: boolean;
  // Private creator string when setting private tags
  PrivateCreator?: string;
  // Allow setting protected tags
  Force?: boolean;
}

// Body for POST /modalities/{id}/store
export interface OrthancStoreModalityRequest {
  // Array of Orthanc resource IDs to send via C-STORE
  Resources: string[];
  // If true, run as a background job
  Asynchronous?: boolean;
  // If true, propose compression transfer syntaxes
  Compress?: boolean;
  // Override local AET for this transfer
  LocalAet?: string;
  // Set Move Originator AET in C-STORE request headers
  MoveOriginatorAet?: string;
  // Set Move Originator Message ID in C-STORE request headers
  MoveOriginatorID?: number;
  // If true, ignore individual instance failures and continue
  Permissive?: boolean;
  // If true, trigger a storage commitment request after the store completes
  StorageCommitment?: boolean;
  // Timeout in seconds for the SCU association (0 = no timeout)
  Timeout?: number;
}

// Body for POST /modalities/{id}/move
export interface OrthancMoveModalityRequest {
  // DICOM query level for the C-MOVE
  Level: 'Patient' | 'Study' | 'Series' | 'Instance';
  // DICOM tag criteria identifying what to move (array of tag-value objects)
  Resources: Array<Record<string, string>>;
  // Target AET that will receive the C-MOVE results
  TargetAet: string;
  // Timeout in seconds for the SCU association
  Timeout?: number;
  // If true, run as a background job
  Asynchronous?: boolean;
  // Job priority (0-255)
  Priority?: number;
}

// Body for POST /modalities/{id}/query
export interface OrthancQueryModalityRequest {
  // DICOM query level for the C-FIND
  Level: 'Patient' | 'Study' | 'Series' | 'Instance' | 'Worklist';
  // DICOM tag filters (keys are hex tags or names, values are match patterns with wildcards)
  Query: Record<string, string>;
  // If true, normalize the query according to the DICOM standard
  Normalize?: boolean;
  // Timeout in seconds for the SCU association
  Timeout?: number;
}

// Query params for GET /patients/{id}, /studies/{id}, /series/{id}, /instances/{id}
// and all list endpoints (in addition to OrthancListOptions)
export interface OrthancResourceGetOptions {
  // If true, report DICOM tags in full format (hex key + symbolic name + value)
  full?: boolean;
  // If true, report DICOM tags in hexadecimal short format
  short?: boolean;
  // Semicolon-separated list of extra DICOM tags to include in response
  // e.g. "0010,0010;PatientBirthDate". Returned in "RequestedTags" field.
  // Slow if tags aren't in MainDicomTags (requires reading DICOM files)
  requestedTags?: string;
  // Defines response content (new in 1.12.5, overrides expand)
  // Semicolon-separated: "MainDicomTags", "Metadata", "Children", "Parent",
  // "Labels", "Status", "IsStable", "IsProtected", "Attachments"
  responseContent?: string;
}

// Query params for image-uint16, image-int16, image-uint8, preview endpoints
export interface OrthancImageOptions {
  // JPEG quality (1-100, default 90)
  quality?: number;
  // If true, return placeholder PNG instead of 415 error for unsupported images
  returnUnsupportedImage?: boolean;
  // Output format: "image/png" (default), "image/jpeg", "image/x-portable-arbitrarymap"
  accept?: string;
}

// Query params for GET /instances/{id}/file
export interface OrthancFileDownloadOptions {
  // Filename for Content-Disposition header
  filename?: string;
  // Transcode to this transfer syntax UID
  transcode?: string;
  // Quality for lossy transcoding (1-100)
  lossyQuality?: number;
}

// Query params for numpy endpoints
export interface OrthancNumpyOptions {
  // Compress output as .npz
  compress?: boolean;
  // Apply RescaleSlope/Intercept and return floating-point values
  rescale?: boolean;
}

// ============ Base API Client ============

const _fetchTimeout = 60_000;
const _fetchBinaryResponseTimeout = 15_000;
const _fetchBinaryDownloadTimeout = 60_000;
const _fetchTextTimeout = 60_000;

class OrthancAPIClient {
  constructor(private dicomArchiveConfig: DicomArchiveConfig) {}

  private async fetch<T>(
    path: string,
    options?: {
      method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
      body?: unknown;
    },
  ): Promise<T | null> {
    const { method = 'GET', body } = options || {};
    const token = await orthancJwt(this.dicomArchiveConfig.id);
    const baseUrl = `${this.dicomArchiveConfig.url}/..`;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), _fetchTimeout);
    try {
      const resp = await fetch(`${baseUrl}${path}`, {
        signal: controller.signal,
        method,
        // Avoid a proxy/browser cache serving stale study status between polls.
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          ...(token ? { token } : {}),
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });

      if (!resp.ok) {
        console.warn(`Orthanc API error [${resp.status}]: ${method} ${path}`);
        return null;
      }

      return (await resp.json()) as T;
    }
    catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        console.warn(`Orthanc API request timed out: ${path}`);
        return null;
      }
      console.error(`Orthanc API error: ${path}`, e);
      return null;
    }
    finally {
      clearTimeout(timeout);
    }
  }

  // Binary fetch for endpoints that return non-JSON data (images, DICOM files, archives)
  private async fetchBinary(
    path: string,
    options?: { method?: 'GET' | 'POST'; body?: unknown; accept?: string },
  ): Promise<ArrayBuffer | null> {
    const { method = 'GET', body, accept } = options || {};
    const token = await orthancJwt(this.dicomArchiveConfig.id);
    const baseUrl = `${this.dicomArchiveConfig.url}/..`;

    const controller = new AbortController();
    const timeout = setTimeout(
      () => controller.abort(),
      _fetchBinaryResponseTimeout,
    );
    try {
      const resp = await fetch(`${baseUrl}${path}`, {
        signal: controller.signal,
        method,
        headers: {
          ...(accept ? { Accept: accept } : {}),
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          ...(token ? { token } : {}),
          ...(body ? { 'Content-Type': 'application/json' } : {}),
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      clearTimeout(timeout);

      if (!resp.ok) {
        console.warn(
          `Orthanc API binary error [${resp.status}]: ${method} ${path}`,
        );
        return null;
      }

      const downloadTimeout = setTimeout(
        () => controller.abort(),
        _fetchBinaryDownloadTimeout,
      );
      try {
        return await resp.arrayBuffer();
      }
      finally {
        clearTimeout(downloadTimeout);
      }
    }
    catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        console.warn(`Orthanc API binary request timed out: ${path}`);
        return null;
      }
      console.error(`Orthanc API binary error: ${path}`, e);
      return null;
    }
  }

  // Text fetch for endpoints that return plain text (metadata values, encoding, etc.)
  private async fetchText(
    path: string,
    options?: { method?: 'GET' | 'PUT'; body?: string },
  ): Promise<string | null> {
    const { method = 'GET', body } = options || {};
    const token = await orthancJwt(this.dicomArchiveConfig.id);
    const baseUrl = `${this.dicomArchiveConfig.url}/..`;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), _fetchTextTimeout);
    try {
      const resp = await fetch(`${baseUrl}${path}`, {
        signal: controller.signal,
        method,
        headers: {
          'Content-Type': 'text/plain',
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          ...(token ? { token } : {}),
        },
        ...(body !== undefined ? { body } : {}),
      });

      if (!resp.ok) {
        console.warn(
          `Orthanc API text error [${resp.status}]: ${method} ${path}`,
        );
        return null;
      }

      return await resp.text();
    }
    catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        console.warn(`Orthanc API text request timed out: ${path}`);
        return null;
      }
      console.error(`Orthanc API text error: ${path}`, e);
      return null;
    }
    finally {
      clearTimeout(timeout);
    }
  }

  // Upload binary data (for POST /instances to upload DICOM files)
  private async upload(
    path: string,
    data: ArrayBuffer,
    contentType = 'application/dicom',
  ): Promise<any | null> {
    const token = await orthancJwt(this.dicomArchiveConfig.id);
    const baseUrl = `${this.dicomArchiveConfig.url}/..`;

    try {
      const resp = await fetch(`${baseUrl}${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': contentType,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          ...(token ? { token } : {}),
        },
        body: data,
      });

      if (!resp.ok) {
        console.warn(`Orthanc API upload error [${resp.status}]: POST ${path}`);
        return null;
      }

      return await resp.json();
    }
    catch (e) {
      console.error(`Orthanc API upload error: ${path}`, e);
      return null;
    }
  }

  // Diagnostic fetch that returns full HTTP error details instead of null.
  // Used by debug tools only — not for production code paths.
  async diagnose(path: string): Promise<{
    status: number;
    statusText: string;
    headers: Record<string, string>;
    body: string;
  }> {
    const token = await orthancJwt(this.dicomArchiveConfig.id);
    const baseUrl = `${this.dicomArchiveConfig.url}/..`;
    const resp = await fetch(`${baseUrl}${path}`, {
      headers: {
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        ...(token ? { token } : {}),
      },
    });
    const headers: Record<string, string> = {};
    resp.headers.forEach((v, k) => {
      headers[k] = v;
    });
    const body = await resp.text();
    return {
      status: resp.status,
      statusText: resp.statusText,
      headers,
      body: body.substring(0, 2000),
    };
  }

  private static buildQs(
    params: Record<string, string | number | boolean | undefined>,
  ): string {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined) qs.set(k, String(v));
    });
    const s = qs.toString();
    return s ? `?${s}` : '';
  }

  // ================================================================
  //  TRACKING CHANGES
  // ================================================================

  // ORTHANC API | GET /changes | List changes
  // @param options.since - Show changes after this sequence number
  // @param options.limit - Maximum number of results
  // @param options.type - Filter by change type, semicolon-separated (requires ExtendedChanges)
  // @param options.last - If true, return only the last change ID (use alone)
  // @param options.to - Show changes up to this sequence number (requires ExtendedChanges)
  async getChanges(options?: {
    since?: number;
    limit?: number;
    type?: string;
    last?: number;
    to?: number;
  }): Promise<OrthancChanges | null> {
    return this.fetch<OrthancChanges>(
      `/changes${OrthancAPIClient.buildQs({
        since: options?.since,
        limit: options?.limit,
        type: options?.type,
        last: options?.last,
        to: options?.to,
      })}`,
    );
  }

  // ORTHANC API | DELETE /changes | Clear changes
  async deleteChanges(): Promise<any> {
    return this.fetch('/changes', { method: 'DELETE' });
  }

  // ORTHANC API | GET /exports | List exports
  // @param since - Sequence number to start from (for pagination)
  // @param limit - Maximum number of results
  async getExports(
    since?: number,
    limit?: number,
  ): Promise<OrthancExports | null> {
    return this.fetch<OrthancExports>(
      `/exports${OrthancAPIClient.buildQs({ since, limit })}`,
    );
  }

  // ORTHANC API | DELETE /exports | Clear exports
  async deleteExports(): Promise<any> {
    return this.fetch('/exports', { method: 'DELETE' });
  }

  // ================================================================
  //  PATIENTS
  // ================================================================

  // ORTHANC API | GET /patients | List the available patients
  // @returns Array of Orthanc patient IDs (short hashes)
  async listPatients(options?: OrthancListOptions): Promise<string[] | null> {
    return this.fetch<string[]>(
      `/patients${OrthancAPIClient.buildQs({
        'since': options?.since,
        'limit': options?.limit,
        'full': options?.full,
        'short': options?.short,
        'requested-tags': options?.requestedTags,
        'response-content': options?.responseContent,
      })}`,
    );
  }

  async listPatientsExpanded(
    options?: OrthancListOptions,
  ): Promise<OrthancPatientInfo[] | null> {
    return this.fetch<OrthancPatientInfo[]>(
      `/patients${OrthancAPIClient.buildQs({
        'expand': true,
        'since': options?.since,
        'limit': options?.limit,
        'full': options?.full,
        'short': options?.short,
        'requested-tags': options?.requestedTags,
        'response-content': options?.responseContent,
      })}`,
    );
  }

  // ORTHANC API | GET /patients/{id} | Get information about some patient
  // @param patientId - Orthanc internal ID (not DICOM PatientID)
  async getPatient(
    patientId: string,
    options?: OrthancResourceGetOptions,
  ): Promise<OrthancPatientInfo | null> {
    return this.fetch<OrthancPatientInfo>(
      `/patients/${patientId}${OrthancAPIClient.buildQs({
        'full': options?.full,
        'short': options?.short,
        'requested-tags': options?.requestedTags,
        'response-content': options?.responseContent,
      })}`,
    );
  }

  // ORTHANC API | DELETE /patients/{id} | Delete some patient
  // @param patientId - Orthanc internal ID
  async deletePatient(patientId: string): Promise<any> {
    return this.fetch(`/patients/${patientId}`, { method: 'DELETE' });
  }

  // ORTHANC API | POST /patients/{id}/anonymize | Anonymize patient
  // @param patientId - Orthanc internal ID
  // @param request - Anonymization options (Replace, Keep, Remove tags)
  async anonymizePatient(
    patientId: string,
    request?: OrthancAnonymizeRequest,
  ): Promise<any> {
    return this.fetch(`/patients/${patientId}/anonymize`, {
      method: 'POST',
      body: request || {},
    });
  }

  // ORTHANC API | GET /patients/{id}/archive | Create ZIP archive
  // @param patientId - Orthanc internal ID
  // @param options - transcode (target transfer syntax UID), filename (attachment filename)
  async getPatientArchive(
    patientId: string,
    options?: OrthancArchiveGetOptions,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/patients/${patientId}/archive${OrthancAPIClient.buildQs({
        transcode: options?.transcode,
        filename: options?.filename,
      })}`,
    );
  }

  // ORTHANC API | POST /patients/{id}/archive | Create ZIP archive (async, returns job)
  // @param patientId - Orthanc internal ID
  // @param options - Asynchronous (run as job), Priority (0-255), Transcode (target transfer syntax)
  async createPatientArchive(
    patientId: string,
    options?: OrthancArchivePostOptions,
  ): Promise<any> {
    return this.fetch(`/patients/${patientId}/archive`, {
      method: 'POST',
      body: options || {},
    });
  }

  // ORTHANC API | GET /patients/{id}/attachments | List attachments
  // @param patientId - Orthanc internal ID
  // @returns Array of attachment names
  async getPatientAttachments(patientId: string): Promise<string[] | null> {
    return this.fetch<string[]>(`/patients/${patientId}/attachments`);
  }

  // ORTHANC API | GET /patients/{id}/attachments?full | List attachments with details
  // @returns Map of attachment name → attachment info (size, MD5, compression, UUID)
  async getPatientAttachmentsFull(
    patientId: string,
  ): Promise<Record<string, OrthancAttachmentInfo> | null> {
    return this.fetch<Record<string, OrthancAttachmentInfo>>(
      `/patients/${patientId}/attachments?full`,
    );
  }

  // ORTHANC API | GET /patients/{id}/attachments/{name} | List operations on attachments
  // @param patientId - Orthanc internal ID
  // @param name - Attachment name (e.g. "dicom-as-json")
  async getPatientAttachment(patientId: string, name: string): Promise<any> {
    return this.fetch(`/patients/${patientId}/attachments/${name}`);
  }

  // ORTHANC API | DELETE /patients/{id}/attachments/{name} | Delete attachment
  async deletePatientAttachment(patientId: string, name: string): Promise<any> {
    return this.fetch(`/patients/${patientId}/attachments/${name}`, {
      method: 'DELETE',
    });
  }

  // ORTHANC API | PUT /patients/{id}/attachments/{name} | Set attachment
  async setPatientAttachment(
    patientId: string,
    name: string,
    data: ArrayBuffer,
  ): Promise<any> {
    return this.upload(
      `/patients/${patientId}/attachments/${name}`,
      data,
      'application/octet-stream',
    );
  }

  // ORTHANC API | POST /patients/{id}/attachments/{name}/compress | Compress attachment
  async compressPatientAttachment(
    patientId: string,
    name: string,
  ): Promise<any> {
    return this.fetch(`/patients/${patientId}/attachments/${name}/compress`, {
      method: 'POST',
    });
  }

  // ORTHANC API | GET /patients/{id}/attachments/{name}/compressed-data | Get attachment (no decompression)
  async getPatientAttachmentCompressedData(
    patientId: string,
    name: string,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/patients/${patientId}/attachments/${name}/compressed-data`,
    );
  }

  // ORTHANC API | GET /patients/{id}/attachments/{name}/compressed-md5 | Get MD5 of attachment on disk
  async getPatientAttachmentCompressedMD5(
    patientId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(
      `/patients/${patientId}/attachments/${name}/compressed-md5`,
    );
  }

  // ORTHANC API | GET /patients/{id}/attachments/{name}/compressed-size | Get size of attachment on disk
  async getPatientAttachmentCompressedSize(
    patientId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(
      `/patients/${patientId}/attachments/${name}/compressed-size`,
    );
  }

  // ORTHANC API | GET /patients/{id}/attachments/{name}/data | Get attachment
  async getPatientAttachmentData(
    patientId: string,
    name: string,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(`/patients/${patientId}/attachments/${name}/data`);
  }

  // ORTHANC API | GET /patients/{id}/attachments/{name}/info | Get info about the attachment
  async getPatientAttachmentInfo(
    patientId: string,
    name: string,
  ): Promise<OrthancAttachmentInfo | null> {
    return this.fetch<OrthancAttachmentInfo>(
      `/patients/${patientId}/attachments/${name}/info`,
    );
  }

  // ORTHANC API | GET /patients/{id}/attachments/{name}/is-compressed | Is attachment compressed?
  async isPatientAttachmentCompressed(
    patientId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(
      `/patients/${patientId}/attachments/${name}/is-compressed`,
    );
  }

  // ORTHANC API | GET /patients/{id}/attachments/{name}/md5 | Get MD5 of attachment
  async getPatientAttachmentMD5(
    patientId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(`/patients/${patientId}/attachments/${name}/md5`);
  }

  // ORTHANC API | GET /patients/{id}/attachments/{name}/size | Get size of attachment
  async getPatientAttachmentSize(
    patientId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(`/patients/${patientId}/attachments/${name}/size`);
  }

  // ORTHANC API | POST /patients/{id}/attachments/{name}/uncompress | Uncompress attachment
  async uncompressPatientAttachment(
    patientId: string,
    name: string,
  ): Promise<any> {
    return this.fetch(`/patients/${patientId}/attachments/${name}/uncompress`, {
      method: 'POST',
    });
  }

  // ORTHANC API | POST /patients/{id}/attachments/{name}/verify-md5 | Verify attachment
  async verifyPatientAttachmentMD5(
    patientId: string,
    name: string,
  ): Promise<any> {
    return this.fetch(`/patients/${patientId}/attachments/${name}/verify-md5`, {
      method: 'POST',
    });
  }

  // ORTHANC API | GET /patients/{id}/instances | Get child instances
  async getPatientInstances(
    patientId: string,
  ): Promise<OrthancInstanceInfo[] | null> {
    return this.fetch<OrthancInstanceInfo[]>(
      `/patients/${patientId}/instances`,
    );
  }

  // ORTHANC API | GET /patients/{id}/instances-tags | Get tags of instances
  // @param options - short/simplify for tag format
  async getPatientInstancesTags(
    patientId: string,
    options?: OrthancTagFormatOptions,
  ): Promise<any> {
    return this.fetch(
      `/patients/${patientId}/instances-tags${OrthancAPIClient.buildQs({
        short: options?.short,
        simplify: options?.simplify,
      })}`,
    );
  }

  // ORTHANC API | GET /patients/{id}/labels | List labels
  async getPatientLabels(patientId: string): Promise<string[] | null> {
    return this.fetch<string[]>(`/patients/${patientId}/labels`);
  }

  // ORTHANC API | GET /patients/{id}/labels/{label} | Test label
  // @returns Empty if label exists (200), 404 if not
  async hasPatientLabel(patientId: string, label: string): Promise<any> {
    return this.fetch(`/patients/${patientId}/labels/${label}`);
  }

  // ORTHANC API | PUT /patients/{id}/labels/{label} | Add label
  async setPatientLabel(patientId: string, label: string): Promise<any> {
    return this.fetch(`/patients/${patientId}/labels/${label}`, {
      method: 'PUT',
    });
  }

  // ORTHANC API | DELETE /patients/{id}/labels/{label} | Remove label
  async removePatientLabel(patientId: string, label: string): Promise<any> {
    return this.fetch(`/patients/${patientId}/labels/${label}`, {
      method: 'DELETE',
    });
  }

  // ORTHANC API | GET /patients/{id}/media | Create DICOMDIR media
  async getPatientMedia(
    patientId: string,
    options?: OrthancArchiveGetOptions,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/patients/${patientId}/media${OrthancAPIClient.buildQs({
        transcode: options?.transcode,
        filename: options?.filename,
      })}`,
    );
  }

  // ORTHANC API | POST /patients/{id}/media | Create DICOMDIR media (async, returns job)
  async createPatientMedia(
    patientId: string,
    options?: OrthancArchivePostOptions,
  ): Promise<any> {
    return this.fetch(`/patients/${patientId}/media`, {
      method: 'POST',
      body: options || {},
    });
  }

  // ORTHANC API | GET /patients/{id}/metadata | List metadata
  async getPatientMetadata(patientId: string): Promise<string[] | null> {
    return this.fetch<string[]>(`/patients/${patientId}/metadata`);
  }

  // ORTHANC API | GET /patients/{id}/metadata?expand | List metadata with values
  async getPatientMetadataExpanded(
    patientId: string,
  ): Promise<OrthancMetadata | null> {
    return this.fetch<OrthancMetadata>(
      `/patients/${patientId}/metadata?expand`,
    );
  }

  // ORTHANC API | GET /patients/{id}/metadata/{name} | Get metadata
  // @param name - Metadata key name
  async getPatientMetadataValue(
    patientId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(`/patients/${patientId}/metadata/${name}`);
  }

  // ORTHANC API | PUT /patients/{id}/metadata/{name} | Set metadata
  async setPatientMetadataValue(
    patientId: string,
    name: string,
    value: string,
  ): Promise<any> {
    return this.fetchText(`/patients/${patientId}/metadata/${name}`, {
      method: 'PUT',
      body: value,
    });
  }

  // ORTHANC API | DELETE /patients/{id}/metadata/{name} | Delete metadata
  async deletePatientMetadataValue(
    patientId: string,
    name: string,
  ): Promise<any> {
    return this.fetch(`/patients/${patientId}/metadata/${name}`, {
      method: 'DELETE',
    });
  }

  // ORTHANC API | POST /patients/{id}/modify | Modify patient
  // @param request - Modification options (Replace, Remove tags)
  async modifyPatient(
    patientId: string,
    request: OrthancModifyRequest,
  ): Promise<any> {
    return this.fetch(`/patients/${patientId}/modify`, {
      method: 'POST',
      body: request,
    });
  }

  // ORTHANC API | GET /patients/{id}/module | Get patient module
  // @param options - short/simplify for tag format
  async getPatientModule<T extends OrthancTagFormatOptions | undefined>(
    patientId: string,
    options?: T,
  ) {
    return this.fetch<TagFormatResult<T>>(
      `/patients/${patientId}/module${OrthancAPIClient.buildQs({
        short: options?.short,
        simplify: options?.simplify,
      })}`,
    );
  }

  // ORTHANC API | GET /patients/{id}/protected | Is the patient protected against recycling?
  async isPatientProtected(patientId: string): Promise<string | null> {
    return this.fetchText(`/patients/${patientId}/protected`);
  }

  // ORTHANC API | PUT /patients/{id}/protected | Protect/unprotect patient against recycling
  // @param protect - "1" to protect, "0" to unprotect
  async setPatientProtected(
    patientId: string,
    protect: '0' | '1',
  ): Promise<any> {
    return this.fetchText(`/patients/${patientId}/protected`, {
      method: 'PUT',
      body: protect,
    });
  }

  // ORTHANC API | POST /patients/{id}/reconstruct | Reconstruct tags & optionally files of patient
  async reconstructPatient(patientId: string, body?: any): Promise<any> {
    return this.fetch(`/patients/${patientId}/reconstruct`, {
      method: 'POST',
      body: body || {},
    });
  }

  // ORTHANC API | GET /patients/{id}/series | Get child series
  async getPatientSeries(
    patientId: string,
  ): Promise<OrthancSeriesInfo[] | null> {
    return this.fetch<OrthancSeriesInfo[]>(`/patients/${patientId}/series`);
  }

  // ORTHANC API | GET /patients/{id}/shared-tags | Get shared tags
  // @param options - short/simplify for tag format
  async getPatientSharedTags<T extends OrthancTagFormatOptions | undefined>(
    patientId: string,
    options?: T,
  ) {
    return this.fetch<TagFormatResult<T>>(
      `/patients/${patientId}/shared-tags${OrthancAPIClient.buildQs({
        short: options?.short,
        simplify: options?.simplify,
      })}`,
    );
  }

  // ORTHANC API | GET /patients/{id}/statistics | Get patient statistics
  async getPatientStatistics(
    patientId: string,
  ): Promise<OrthancStatistics | null> {
    return this.fetch<OrthancStatistics>(`/patients/${patientId}/statistics`);
  }

  // ORTHANC API | GET /patients/{id}/studies | Get child studies
  async getPatientStudies(
    patientId: string,
  ): Promise<OrthancStudyInfo[] | null> {
    return this.fetch<OrthancStudyInfo[]>(`/patients/${patientId}/studies`);
  }

  // ================================================================
  //  STUDIES
  // ================================================================

  // ORTHANC API | GET /studies | List the available studies
  async listStudies(options?: OrthancListOptions): Promise<string[] | null> {
    return this.fetch<string[]>(
      `/studies${OrthancAPIClient.buildQs({
        'since': options?.since,
        'limit': options?.limit,
        'full': options?.full,
        'short': options?.short,
        'requested-tags': options?.requestedTags,
        'response-content': options?.responseContent,
      })}`,
    );
  }

  // ORTHANC API | GET /studies?expand | List studies with details
  async listStudiesExpanded(
    options?: OrthancListOptions,
  ): Promise<OrthancStudyInfo[] | null> {
    return this.fetch<OrthancStudyInfo[]>(
      `/studies${OrthancAPIClient.buildQs({
        'expand': true,
        'since': options?.since,
        'limit': options?.limit,
        'full': options?.full,
        'short': options?.short,
        'requested-tags': options?.requestedTags,
        'response-content': options?.responseContent,
      })}`,
    );
  }

  // ORTHANC API | GET /studies/{id} | Get information about some study
  async getStudy(
    studyId: string,
    options?: OrthancResourceGetOptions,
  ): Promise<OrthancStudyInfo | null> {
    return this.fetch<OrthancStudyInfo>(
      `/studies/${studyId}${OrthancAPIClient.buildQs({
        'full': options?.full,
        'short': options?.short,
        'requested-tags': options?.requestedTags,
        'response-content': options?.responseContent,
      })}`,
    );
  }

  // ORTHANC API | DELETE /studies/{id} | Delete some study
  async deleteStudy(studyId: string): Promise<any> {
    return this.fetch(`/studies/${studyId}`, { method: 'DELETE' });
  }

  // ORTHANC API | POST /studies/{id}/anonymize | Anonymize study
  async anonymizeStudy(
    studyId: string,
    request?: OrthancAnonymizeRequest,
  ): Promise<any> {
    return this.fetch(`/studies/${studyId}/anonymize`, {
      method: 'POST',
      body: request || {},
    });
  }

  // ORTHANC API | GET /studies/{id}/archive | Create ZIP archive
  async getStudyArchive(
    studyId: string,
    options?: OrthancArchiveGetOptions,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/studies/${studyId}/archive${OrthancAPIClient.buildQs({
        transcode: options?.transcode,
        filename: options?.filename,
      })}`,
    );
  }

  // ORTHANC API | POST /studies/{id}/archive | Create ZIP archive (async, returns job)
  async createStudyArchive(
    studyId: string,
    options?: OrthancArchivePostOptions,
  ): Promise<any> {
    return this.fetch(`/studies/${studyId}/archive`, {
      method: 'POST',
      body: options || {},
    });
  }

  // ORTHANC API | POST /ocg19-metadata/rebuild-all | Rebuild all OCG19 metadata
  async rebuildAllOcg19Metadata(): Promise<Record<string, unknown> | null> {
    return this.fetch<Record<string, unknown>>(`/ocg19-metadata/rebuild-all`, {
      method: 'POST',
    });
  }

  // ORTHANC API | GET /studies/{id}/ocg19-metadata | Get OCG19 metadata
  async getStudyOcg19Metadata(studyId: string) {
    return this.fetch<Ocg19InstanceInfo[]>(
      `/studies/${studyId}/ocg19-metadata`,
    );
  }

  // ORTHANC API | GET /studies/{id}/ocg19-metadata?compress=1 | Get OCG19 metadata compressed
  async getStudyOcg19MetadataCompressed(studyId: string) {
    return this.fetch<Ocg19InstanceInfo[]>(
      `/studies/${studyId}/ocg19-metadata?compress=1`,
    );
  }

  // ORTHANC API | POST /studies/{id}/ocg19-metadata | Rebuild OCG19 metadata
  async rebuildStudyOcg19Metadata(studyId: string): Promise<any> {
    return this.fetch(`/studies/${studyId}/ocg19-metadata`, { method: 'POST' });
  }

  // ORTHANC API | GET /studies/{id}/attachments | List attachments
  async getStudyAttachments(studyId: string) {
    return this.fetch<string[]>(`/studies/${studyId}/attachments`);
  }

  // ORTHANC API | GET /studies/{id}/attachments?full | List attachments with details
  async getStudyAttachmentsFull(
    studyId: string,
  ) {
    return this.fetch<Record<string, OrthancAttachmentInfo>>(
      `/studies/${studyId}/attachments?full`,
    );
  }

  // ORTHANC API | GET /studies/{id}/attachments/{name} | List operations on attachments
  async getStudyAttachment(studyId: string, name: string): Promise<any> {
    return this.fetch(`/studies/${studyId}/attachments/${name}`);
  }

  // ORTHANC API | DELETE /studies/{id}/attachments/{name} | Delete attachment
  async deleteStudyAttachment(studyId: string, name: string): Promise<any> {
    return this.fetch(`/studies/${studyId}/attachments/${name}`, {
      method: 'DELETE',
    });
  }

  // ORTHANC API | PUT /studies/{id}/attachments/{name} | Set attachment
  async setStudyAttachment(
    studyId: string,
    name: string,
    data: ArrayBuffer,
  ): Promise<any> {
    return this.upload(
      `/studies/${studyId}/attachments/${name}`,
      data,
      'application/octet-stream',
    );
  }

  // ORTHANC API | POST /studies/{id}/attachments/{name}/compress | Compress attachment
  async compressStudyAttachment(studyId: string, name: string): Promise<any> {
    return this.fetch(`/studies/${studyId}/attachments/${name}/compress`, {
      method: 'POST',
    });
  }

  // ORTHANC API | GET /studies/{id}/attachments/{name}/compressed-data | Get attachment (no decompression)
  async getStudyAttachmentCompressedData(
    studyId: string,
    name: string,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/studies/${studyId}/attachments/${name}/compressed-data`,
    );
  }

  // ORTHANC API | GET /studies/{id}/attachments/{name}/compressed-md5 | Get MD5 of attachment on disk
  async getStudyAttachmentCompressedMD5(
    studyId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(
      `/studies/${studyId}/attachments/${name}/compressed-md5`,
    );
  }

  // ORTHANC API | GET /studies/{id}/attachments/{name}/compressed-size | Get size of attachment on disk
  async getStudyAttachmentCompressedSize(
    studyId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(
      `/studies/${studyId}/attachments/${name}/compressed-size`,
    );
  }

  // ORTHANC API | GET /studies/{id}/attachments/{name}/data | Get attachment
  async getStudyAttachmentData(
    studyId: string,
    name: string,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(`/studies/${studyId}/attachments/${name}/data`);
  }

  // ORTHANC API | GET /studies/{id}/attachments/{name}/info | Get info about the attachment
  async getStudyAttachmentInfo(
    studyId: string,
    name: string,
  ): Promise<OrthancAttachmentInfo | null> {
    return this.fetch<OrthancAttachmentInfo>(
      `/studies/${studyId}/attachments/${name}/info`,
    );
  }

  // ORTHANC API | GET /studies/{id}/attachments/{name}/is-compressed | Is attachment compressed?
  async isStudyAttachmentCompressed(
    studyId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(
      `/studies/${studyId}/attachments/${name}/is-compressed`,
    );
  }

  // ORTHANC API | GET /studies/{id}/attachments/{name}/md5 | Get MD5 of attachment
  async getStudyAttachmentMD5(
    studyId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(`/studies/${studyId}/attachments/${name}/md5`);
  }

  // ORTHANC API | GET /studies/{id}/attachments/{name}/size | Get size of attachment
  async getStudyAttachmentSize(
    studyId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(`/studies/${studyId}/attachments/${name}/size`);
  }

  // ORTHANC API | POST /studies/{id}/attachments/{name}/uncompress | Uncompress attachment
  async uncompressStudyAttachment(studyId: string, name: string): Promise<any> {
    return this.fetch(`/studies/${studyId}/attachments/${name}/uncompress`, {
      method: 'POST',
    });
  }

  // ORTHANC API | POST /studies/{id}/attachments/{name}/verify-md5 | Verify attachment
  async verifyStudyAttachmentMD5(studyId: string, name: string): Promise<any> {
    return this.fetch(`/studies/${studyId}/attachments/${name}/verify-md5`, {
      method: 'POST',
    });
  }

  // ORTHANC API | GET /studies/{id}/instances | Get child instances
  async getStudyInstances(
    studyId: string,
  ): Promise<OrthancInstanceInfo[] | null> {
    return this.fetch<OrthancInstanceInfo[]>(`/studies/${studyId}/instances`);
  }

  // ORTHANC API | GET /studies/{id}/instances-tags | Get tags of instances
  async getStudyInstancesTags<T extends OrthancTagFormatOptions | undefined>(
    studyId: string,
    options?: T,
  ) {
    return this.fetch<TagFormatResult<T>>(
      `/studies/${studyId}/instances-tags${OrthancAPIClient.buildQs({
        short: options?.short,
        simplify: options?.simplify,
      })}`,
    );
  }

  // ORTHANC API | GET /studies/{id}/labels | List labels
  async getStudyLabels(studyId: string): Promise<string[] | null> {
    return this.fetch<string[]>(`/studies/${studyId}/labels`);
  }

  // ORTHANC API | GET /studies/{id}/labels/{label} | Test label
  async hasStudyLabel(studyId: string, label: string): Promise<any> {
    return this.fetch(`/studies/${studyId}/labels/${label}`);
  }

  // ORTHANC API | PUT /studies/{id}/labels/{label} | Add label
  async setStudyLabel(studyId: string, label: string): Promise<any> {
    return this.fetch(`/studies/${studyId}/labels/${label}`, { method: 'PUT' });
  }

  // ORTHANC API | DELETE /studies/{id}/labels/{label} | Remove label
  async removeStudyLabel(studyId: string, label: string): Promise<any> {
    return this.fetch(`/studies/${studyId}/labels/${label}`, {
      method: 'DELETE',
    });
  }

  // ORTHANC API | GET /studies/{id}/media | Create DICOMDIR media
  async getStudyMedia(
    studyId: string,
    options?: OrthancArchiveGetOptions,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/studies/${studyId}/media${OrthancAPIClient.buildQs({
        transcode: options?.transcode,
        filename: options?.filename,
      })}`,
    );
  }

  // ORTHANC API | POST /studies/{id}/media | Create DICOMDIR media (async, returns job)
  async createStudyMedia(
    studyId: string,
    options?: OrthancArchivePostOptions,
  ): Promise<any> {
    return this.fetch(`/studies/${studyId}/media`, {
      method: 'POST',
      body: options || {},
    });
  }

  // ORTHANC API | POST /studies/{id}/merge | Merge study
  // @param body - { Resources: string[] } array of Orthanc IDs of studies to merge into this one
  async mergeStudy(
    studyId: string,
    body: { Resources: string[] },
  ): Promise<any> {
    return this.fetch(`/studies/${studyId}/merge`, { method: 'POST', body });
  }

  // ORTHANC API | GET /studies/{id}/metadata | List metadata
  async getStudyMetadata(studyId: string): Promise<string[] | null> {
    return this.fetch<string[]>(`/studies/${studyId}/metadata`);
  }

  // ORTHANC API | GET /studies/{id}/metadata?expand | List metadata with values
  async getStudyMetadataExpanded(
    studyId: string,
  ): Promise<OrthancMetadata | null> {
    return this.fetch<OrthancMetadata>(`/studies/${studyId}/metadata?expand`);
  }

  // ORTHANC API | GET /studies/{id}/metadata/{name} | Get metadata
  async getStudyMetadataValue(
    studyId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(`/studies/${studyId}/metadata/${name}`);
  }

  // ORTHANC API | PUT /studies/{id}/metadata/{name} | Set metadata
  async setStudyMetadataValue(
    studyId: string,
    name: string,
    value: string,
  ): Promise<any> {
    return this.fetchText(`/studies/${studyId}/metadata/${name}`, {
      method: 'PUT',
      body: value,
    });
  }

  // ORTHANC API | DELETE /studies/{id}/metadata/{name} | Delete metadata
  async deleteStudyMetadataValue(studyId: string, name: string): Promise<any> {
    return this.fetch(`/studies/${studyId}/metadata/${name}`, {
      method: 'DELETE',
    });
  }

  // ORTHANC API | POST /studies/{id}/modify | Modify study
  async modifyStudy(
    studyId: string,
    request: OrthancModifyRequest,
  ): Promise<any> {
    return this.fetch(`/studies/${studyId}/modify`, {
      method: 'POST',
      body: request,
    });
  }

  // ORTHANC API | GET /studies/{id}/module | Get study module
  async getStudyModule<T extends OrthancTagFormatOptions | undefined>(
    studyId: string,
    options?: T,
  ) {
    return this.fetch<TagFormatResult<T>>(
      `/studies/${studyId}/module${OrthancAPIClient.buildQs({
        short: options?.short,
        simplify: options?.simplify,
      })}`,
    );
  }

  // ORTHANC API | GET /studies/{id}/module-patient | Get patient module of study
  async getStudyPatientModule<T extends OrthancTagFormatOptions | undefined>(
    studyId: string,
    options?: T,
  ) {
    return this.fetch<TagFormatResult<T>>(
      `/studies/${studyId}/module-patient${OrthancAPIClient.buildQs({
        short: options?.short,
        simplify: options?.simplify,
      })}`,
    );
  }

  // ORTHANC API | GET /studies/{id}/patient | Get parent patient
  async getStudyParentPatient(
    studyId: string,
  ): Promise<OrthancPatientInfo | null> {
    return this.fetch<OrthancPatientInfo>(`/studies/${studyId}/patient`);
  }

  // ORTHANC API | POST /studies/{id}/reconstruct | Reconstruct tags & optionally files of study
  async reconstructStudy(studyId: string, body?: any): Promise<any> {
    return this.fetch(`/studies/${studyId}/reconstruct`, {
      method: 'POST',
      body: body || {},
    });
  }

  // ORTHANC API | GET /studies/{id}/series | Get child series (IDs only)
  async getStudySeries(studyId: string): Promise<string[] | null> {
    return this.fetch<string[]>(`/studies/${studyId}/series`);
  }

  // ORTHANC API | GET /studies/{id}/series?expand | Get child series with details
  async getStudySeriesExpanded(
    studyId: string,
  ): Promise<OrthancSeriesInfo[] | null> {
    return this.fetch<OrthancSeriesInfo[]>(`/studies/${studyId}/series?expand`);
  }

  // ORTHANC API | GET /studies/{id}/shared-tags | Get shared tags
  async getStudySharedTags<T extends OrthancTagFormatOptions | undefined>(
    studyId: string,
    options?: T,
  ) {
    return this.fetch<TagFormatResult<T>>(
      `/studies/${studyId}/shared-tags${OrthancAPIClient.buildQs({
        short: options?.short,
        simplify: options?.simplify,
      })}`,
    );
  }

  // ORTHANC API | POST /studies/{id}/split | Split study
  // @param body - { Series: string[], KeepSource: boolean, ... }
  async splitStudy(studyId: string, body: any): Promise<any> {
    return this.fetch(`/studies/${studyId}/split`, { method: 'POST', body });
  }

  // ORTHANC API | GET /studies/{id}/statistics | Get study statistics
  async getStudyStatistics(studyId: string): Promise<OrthancStatistics | null> {
    return this.fetch<OrthancStatistics>(`/studies/${studyId}/statistics`);
  }

  // ================================================================
  //  SERIES
  // ================================================================

  // ORTHANC API | GET /series | List the available series
  async listSeries(options?: OrthancListOptions): Promise<string[] | null> {
    return this.fetch<string[]>(
      `/series${OrthancAPIClient.buildQs({
        'since': options?.since,
        'limit': options?.limit,
        'full': options?.full,
        'short': options?.short,
        'requested-tags': options?.requestedTags,
        'response-content': options?.responseContent,
      })}`,
    );
  }

  // ORTHANC API | GET /series?expand | List series with details
  async listSeriesExpanded(
    options?: OrthancListOptions,
  ): Promise<OrthancSeriesInfo[] | null> {
    return this.fetch<OrthancSeriesInfo[]>(
      `/series${OrthancAPIClient.buildQs({
        'expand': true,
        'since': options?.since,
        'limit': options?.limit,
        'full': options?.full,
        'short': options?.short,
        'requested-tags': options?.requestedTags,
        'response-content': options?.responseContent,
      })}`,
    );
  }

  // ORTHANC API | GET /series/{id} | Get information about some series
  async getSeries(
    seriesId: string,
    options?: OrthancResourceGetOptions,
  ): Promise<OrthancSeriesInfo | null> {
    return this.fetch<OrthancSeriesInfo>(
      `/series/${seriesId}${OrthancAPIClient.buildQs({
        'full': options?.full,
        'short': options?.short,
        'requested-tags': options?.requestedTags,
        'response-content': options?.responseContent,
      })}`,
    );
  }

  // ORTHANC API | DELETE /series/{id} | Delete some series
  async deleteSeries(seriesId: string): Promise<any> {
    return this.fetch(`/series/${seriesId}`, { method: 'DELETE' });
  }

  // ORTHANC API | POST /series/{id}/anonymize | Anonymize series
  async anonymizeSeries(
    seriesId: string,
    request?: OrthancAnonymizeRequest,
  ): Promise<any> {
    return this.fetch(`/series/${seriesId}/anonymize`, {
      method: 'POST',
      body: request || {},
    });
  }

  // ORTHANC API | GET /series/{id}/archive | Create ZIP archive
  async getSeriesArchive(
    seriesId: string,
    options?: OrthancArchiveGetOptions,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/series/${seriesId}/archive${OrthancAPIClient.buildQs({
        transcode: options?.transcode,
        filename: options?.filename,
      })}`,
    );
  }

  // ORTHANC API | POST /series/{id}/archive | Create ZIP archive (async, returns job)
  async createSeriesArchive(
    seriesId: string,
    options?: OrthancArchivePostOptions,
  ): Promise<any> {
    return this.fetch(`/series/${seriesId}/archive`, {
      method: 'POST',
      body: options || {},
    });
  }

  // ORTHANC API | GET /series/{id}/attachments | List attachments
  async getSeriesAttachments(seriesId: string): Promise<string[] | null> {
    return this.fetch<string[]>(`/series/${seriesId}/attachments`);
  }

  // ORTHANC API | GET /series/{id}/attachments?full | List attachments with details
  async getSeriesAttachmentsFull(
    seriesId: string,
  ): Promise<Record<string, OrthancAttachmentInfo> | null> {
    return this.fetch<Record<string, OrthancAttachmentInfo>>(
      `/series/${seriesId}/attachments?full`,
    );
  }

  // ORTHANC API | GET /series/{id}/attachments/{name} | List operations on attachments
  async getSeriesAttachment(seriesId: string, name: string): Promise<any> {
    return this.fetch(`/series/${seriesId}/attachments/${name}`);
  }

  // ORTHANC API | DELETE /series/{id}/attachments/{name} | Delete attachment
  async deleteSeriesAttachment(seriesId: string, name: string): Promise<any> {
    return this.fetch(`/series/${seriesId}/attachments/${name}`, {
      method: 'DELETE',
    });
  }

  // ORTHANC API | PUT /series/{id}/attachments/{name} | Set attachment
  async setSeriesAttachment(
    seriesId: string,
    name: string,
    data: ArrayBuffer,
  ): Promise<any> {
    return this.upload(
      `/series/${seriesId}/attachments/${name}`,
      data,
      'application/octet-stream',
    );
  }

  // ORTHANC API | POST /series/{id}/attachments/{name}/compress | Compress attachment
  async compressSeriesAttachment(seriesId: string, name: string): Promise<any> {
    return this.fetch(`/series/${seriesId}/attachments/${name}/compress`, {
      method: 'POST',
    });
  }

  // ORTHANC API | GET /series/{id}/attachments/{name}/compressed-data | Get attachment (no decompression)
  async getSeriesAttachmentCompressedData(
    seriesId: string,
    name: string,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/series/${seriesId}/attachments/${name}/compressed-data`,
    );
  }

  // ORTHANC API | GET /series/{id}/attachments/{name}/compressed-md5 | Get MD5 of attachment on disk
  async getSeriesAttachmentCompressedMD5(
    seriesId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(
      `/series/${seriesId}/attachments/${name}/compressed-md5`,
    );
  }

  // ORTHANC API | GET /series/{id}/attachments/{name}/compressed-size | Get size of attachment on disk
  async getSeriesAttachmentCompressedSize(
    seriesId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(
      `/series/${seriesId}/attachments/${name}/compressed-size`,
    );
  }

  // ORTHANC API | GET /series/{id}/attachments/{name}/data | Get attachment
  async getSeriesAttachmentData(
    seriesId: string,
    name: string,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(`/series/${seriesId}/attachments/${name}/data`);
  }

  // ORTHANC API | GET /series/{id}/attachments/{name}/info | Get info about the attachment
  async getSeriesAttachmentInfo(
    seriesId: string,
    name: string,
  ): Promise<OrthancAttachmentInfo | null> {
    return this.fetch<OrthancAttachmentInfo>(
      `/series/${seriesId}/attachments/${name}/info`,
    );
  }

  // ORTHANC API | GET /series/{id}/attachments/{name}/is-compressed | Is attachment compressed?
  async isSeriesAttachmentCompressed(
    seriesId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(
      `/series/${seriesId}/attachments/${name}/is-compressed`,
    );
  }

  // ORTHANC API | GET /series/{id}/attachments/{name}/md5 | Get MD5 of attachment
  async getSeriesAttachmentMD5(
    seriesId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(`/series/${seriesId}/attachments/${name}/md5`);
  }

  // ORTHANC API | GET /series/{id}/attachments/{name}/size | Get size of attachment
  async getSeriesAttachmentSize(
    seriesId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(`/series/${seriesId}/attachments/${name}/size`);
  }

  // ORTHANC API | POST /series/{id}/attachments/{name}/uncompress | Uncompress attachment
  async uncompressSeriesAttachment(
    seriesId: string,
    name: string,
  ): Promise<any> {
    return this.fetch(`/series/${seriesId}/attachments/${name}/uncompress`, {
      method: 'POST',
    });
  }

  // ORTHANC API | POST /series/{id}/attachments/{name}/verify-md5 | Verify attachment
  async verifySeriesAttachmentMD5(
    seriesId: string,
    name: string,
  ): Promise<any> {
    return this.fetch(`/series/${seriesId}/attachments/${name}/verify-md5`, {
      method: 'POST',
    });
  }

  // ORTHANC API | GET /series/{id}/instances | Get child instances (IDs only)
  async getSeriesInstances(seriesId: string): Promise<string[] | null> {
    return this.fetch<string[]>(`/series/${seriesId}/instances`);
  }

  // ORTHANC API | GET /series/{id}/instances?expand | Get child instances with details
  async getSeriesInstancesExpanded(
    seriesId: string,
  ): Promise<OrthancInstanceInfo[] | null> {
    return this.fetch<OrthancInstanceInfo[]>(
      `/series/${seriesId}/instances?expand`,
    );
  }

  // ORTHANC API | GET /series/{id}/instances-tags | Get tags of instances
  async getSeriesInstancesTags<T extends OrthancTagFormatOptions | undefined>(
    seriesId: string,
    options?: T,
  ) {
    return this.fetch<TagFormatResult<T>>(
      `/series/${seriesId}/instances-tags${OrthancAPIClient.buildQs({
        short: options?.short,
        simplify: options?.simplify,
      })}`,
    );
  }

  // ORTHANC API | GET /series/{id}/labels | List labels
  async getSeriesLabels(seriesId: string): Promise<string[] | null> {
    return this.fetch<string[]>(`/series/${seriesId}/labels`);
  }

  // ORTHANC API | GET /series/{id}/labels/{label} | Test label
  async hasSeriesLabel(seriesId: string, label: string): Promise<any> {
    return this.fetch(`/series/${seriesId}/labels/${label}`);
  }

  // ORTHANC API | PUT /series/{id}/labels/{label} | Add label
  async setSeriesLabel(seriesId: string, label: string): Promise<any> {
    return this.fetch(`/series/${seriesId}/labels/${label}`, { method: 'PUT' });
  }

  // ORTHANC API | DELETE /series/{id}/labels/{label} | Remove label
  async removeSeriesLabel(seriesId: string, label: string): Promise<any> {
    return this.fetch(`/series/${seriesId}/labels/${label}`, {
      method: 'DELETE',
    });
  }

  // ORTHANC API | GET /series/{id}/media | Create DICOMDIR media
  async getSeriesMedia(
    seriesId: string,
    options?: OrthancArchiveGetOptions,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/series/${seriesId}/media${OrthancAPIClient.buildQs({
        transcode: options?.transcode,
        filename: options?.filename,
      })}`,
    );
  }

  // ORTHANC API | POST /series/{id}/media | Create DICOMDIR media (async, returns job)
  async createSeriesMedia(
    seriesId: string,
    options?: OrthancArchivePostOptions,
  ): Promise<any> {
    return this.fetch(`/series/${seriesId}/media`, {
      method: 'POST',
      body: options || {},
    });
  }

  // ORTHANC API | GET /series/{id}/metadata | List metadata
  async getSeriesMetadata(seriesId: string): Promise<string[] | null> {
    return this.fetch<string[]>(`/series/${seriesId}/metadata`);
  }

  // ORTHANC API | GET /series/{id}/metadata?expand | List metadata with values
  async getSeriesMetadataExpanded(
    seriesId: string,
  ): Promise<OrthancMetadata | null> {
    return this.fetch<OrthancMetadata>(`/series/${seriesId}/metadata?expand`);
  }

  // ORTHANC API | GET /series/{id}/metadata/{name} | Get metadata
  async getSeriesMetadataValue(
    seriesId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(`/series/${seriesId}/metadata/${name}`);
  }

  // ORTHANC API | PUT /series/{id}/metadata/{name} | Set metadata
  async setSeriesMetadataValue(
    seriesId: string,
    name: string,
    value: string,
  ): Promise<any> {
    return this.fetchText(`/series/${seriesId}/metadata/${name}`, {
      method: 'PUT',
      body: value,
    });
  }

  // ORTHANC API | DELETE /series/{id}/metadata/{name} | Delete metadata
  async deleteSeriesMetadataValue(
    seriesId: string,
    name: string,
  ): Promise<any> {
    return this.fetch(`/series/${seriesId}/metadata/${name}`, {
      method: 'DELETE',
    });
  }

  // ORTHANC API | POST /series/{id}/modify | Modify series
  async modifySeries(
    seriesId: string,
    request: OrthancModifyRequest,
  ): Promise<any> {
    return this.fetch(`/series/${seriesId}/modify`, {
      method: 'POST',
      body: request,
    });
  }

  // ORTHANC API | GET /series/{id}/module | Get series module
  async getSeriesModule<T extends OrthancTagFormatOptions | undefined>(
    seriesId: string,
    options?: T,
  ) {
    return this.fetch<TagFormatResult<T>>(
      `/series/${seriesId}/module${OrthancAPIClient.buildQs({
        short: options?.short,
        simplify: options?.simplify,
      })}`,
    );
  }

  // ORTHANC API | GET /series/{id}/numpy | Decode series for numpy
  async getSeriesNumpy(
    seriesId: string,
    options?: OrthancNumpyOptions,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/series/${seriesId}/numpy${OrthancAPIClient.buildQs({
        compress: options?.compress,
        rescale: options?.rescale,
      })}`,
    );
  }

  // ORTHANC API | GET /series/{id}/ordered-slices | (deprecated) Order the slices
  async getSeriesOrderedSlices(seriesId: string) {
    return this.fetch<Record<string, unknown>>(`/series/${seriesId}/ordered-slices`);
  }

  // ORTHANC API | GET /series/{id}/patient | Get parent patient
  async getSeriesParentPatient(
    seriesId: string,
  ): Promise<OrthancPatientInfo | null> {
    return this.fetch<OrthancPatientInfo>(`/series/${seriesId}/patient`);
  }

  // ORTHANC API | POST /series/{id}/reconstruct | Reconstruct tags & optionally files of series
  async reconstructSeries(seriesId: string, body?: any): Promise<any> {
    return this.fetch(`/series/${seriesId}/reconstruct`, {
      method: 'POST',
      body: body || {},
    });
  }

  // ORTHANC API | GET /series/{id}/shared-tags | Get shared tags
  async getSeriesSharedTags<T extends OrthancTagFormatOptions | undefined>(
    seriesId: string,
    options?: T,
  ) {
    return this.fetch<TagFormatResult<T>>(
      `/series/${seriesId}/shared-tags${OrthancAPIClient.buildQs({
        short: options?.short,
        simplify: options?.simplify,
      })}`,
    );
  }

  // ORTHANC API | GET /series/{id}/statistics | Get series statistics
  async getSeriesStatistics(
    seriesId: string,
  ): Promise<OrthancStatistics | null> {
    return this.fetch<OrthancStatistics>(`/series/${seriesId}/statistics`);
  }

  // ORTHANC API | GET /series/{id}/study | Get parent study
  async getSeriesParentStudy(
    seriesId: string,
  ): Promise<OrthancStudyInfo | null> {
    return this.fetch<OrthancStudyInfo>(`/series/${seriesId}/study`);
  }

  // ================================================================
  //  INSTANCES
  // ================================================================

  // ORTHANC API | GET /instances | List the available instances
  async listInstances(options?: OrthancListOptions): Promise<string[] | null> {
    return this.fetch<string[]>(
      `/instances${OrthancAPIClient.buildQs({
        'since': options?.since,
        'limit': options?.limit,
        'full': options?.full,
        'short': options?.short,
        'requested-tags': options?.requestedTags,
        'response-content': options?.responseContent,
      })}`,
    );
  }

  // ORTHANC API | GET /instances?expand | List instances with details
  async listInstancesExpanded(
    options?: OrthancListOptions,
  ): Promise<OrthancInstanceInfo[] | null> {
    return this.fetch<OrthancInstanceInfo[]>(
      `/instances${OrthancAPIClient.buildQs({
        'expand': true,
        'since': options?.since,
        'limit': options?.limit,
        'full': options?.full,
        'short': options?.short,
        'requested-tags': options?.requestedTags,
        'response-content': options?.responseContent,
      })}`,
    );
  }

  // ORTHANC API | POST /instances | Upload a DICOM instance
  // @param dicomData - Raw DICOM file as ArrayBuffer
  async uploadInstance(dicomData: ArrayBuffer): Promise<any> {
    return this.upload('/instances', dicomData, 'application/dicom');
  }

  // ORTHANC API | POST /instances | Upload DICOM instances from a ZIP archive
  // Orthanc auto-detects ZIP vs single DICOM. Extracts and imports all DICOM files found.
  // @param zipData - ZIP archive containing DICOM files as ArrayBuffer
  async uploadInstancesZip(zipData: ArrayBuffer): Promise<any> {
    return this.upload('/instances', zipData, 'application/zip');
  }

  // ORTHANC API | GET /instances/{id} | Get information about some instance
  async getInstance(
    instanceId: string,
    options?: OrthancResourceGetOptions,
  ): Promise<OrthancInstanceInfo | null> {
    return this.fetch<OrthancInstanceInfo>(
      `/instances/${instanceId}${OrthancAPIClient.buildQs({
        'full': options?.full,
        'short': options?.short,
        'requested-tags': options?.requestedTags,
        'response-content': options?.responseContent,
      })}`,
    );
  }

  // ORTHANC API | DELETE /instances/{id} | Delete some instance
  async deleteInstance(instanceId: string): Promise<any> {
    return this.fetch(`/instances/${instanceId}`, { method: 'DELETE' });
  }

  // ORTHANC API | POST /instances/{id}/anonymize | Anonymize instance
  // @returns New anonymized DICOM instance (binary)
  async anonymizeInstance(
    instanceId: string,
    request?: OrthancAnonymizeRequest,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(`/instances/${instanceId}/anonymize`, {
      method: 'POST',
      body: request || {},
    });
  }

  // ORTHANC API | GET /instances/{id}/attachments | List attachments
  async getInstanceAttachments(instanceId: string): Promise<string[] | null> {
    return this.fetch<string[]>(`/instances/${instanceId}/attachments`);
  }

  // ORTHANC API | GET /instances/{id}/attachments?full | List attachments with details
  async getInstanceAttachmentsFull(
    instanceId: string,
  ): Promise<Record<string, OrthancAttachmentInfo> | null> {
    return this.fetch<Record<string, OrthancAttachmentInfo>>(
      `/instances/${instanceId}/attachments?full`,
    );
  }

  // ORTHANC API | GET /instances/{id}/attachments/{name} | List operations on attachments
  async getInstanceAttachment(instanceId: string, name: string): Promise<any> {
    return this.fetch(`/instances/${instanceId}/attachments/${name}`);
  }

  // ORTHANC API | DELETE /instances/{id}/attachments/{name} | Delete attachment
  async deleteInstanceAttachment(
    instanceId: string,
    name: string,
  ): Promise<any> {
    return this.fetch(`/instances/${instanceId}/attachments/${name}`, {
      method: 'DELETE',
    });
  }

  // ORTHANC API | PUT /instances/{id}/attachments/{name} | Set attachment
  async setInstanceAttachment(
    instanceId: string,
    name: string,
    data: ArrayBuffer,
  ): Promise<any> {
    return this.upload(
      `/instances/${instanceId}/attachments/${name}`,
      data,
      'application/octet-stream',
    );
  }

  // ORTHANC API | POST /instances/{id}/attachments/{name}/compress | Compress attachment
  async compressInstanceAttachment(
    instanceId: string,
    name: string,
  ): Promise<any> {
    return this.fetch(`/instances/${instanceId}/attachments/${name}/compress`, {
      method: 'POST',
    });
  }

  // ORTHANC API | GET /instances/{id}/attachments/{name}/compressed-data | Get attachment (no decompression)
  async getInstanceAttachmentCompressedData(
    instanceId: string,
    name: string,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/instances/${instanceId}/attachments/${name}/compressed-data`,
    );
  }

  // ORTHANC API | GET /instances/{id}/attachments/{name}/compressed-md5 | Get MD5 of attachment on disk
  async getInstanceAttachmentCompressedMD5(
    instanceId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(
      `/instances/${instanceId}/attachments/${name}/compressed-md5`,
    );
  }

  // ORTHANC API | GET /instances/{id}/attachments/{name}/compressed-size | Get size of attachment on disk
  async getInstanceAttachmentCompressedSize(
    instanceId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(
      `/instances/${instanceId}/attachments/${name}/compressed-size`,
    );
  }

  // ORTHANC API | GET /instances/{id}/attachments/{name}/data | Get attachment
  async getInstanceAttachmentData(
    instanceId: string,
    name: string,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/instances/${instanceId}/attachments/${name}/data`,
    );
  }

  // ORTHANC API | GET /instances/{id}/attachments/{name}/info | Get info about the attachment
  async getInstanceAttachmentInfo(
    instanceId: string,
    name: string,
  ): Promise<OrthancAttachmentInfo | null> {
    return this.fetch<OrthancAttachmentInfo>(
      `/instances/${instanceId}/attachments/${name}/info`,
    );
  }

  // ORTHANC API | GET /instances/{id}/attachments/{name}/is-compressed | Is attachment compressed?
  async isInstanceAttachmentCompressed(
    instanceId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(
      `/instances/${instanceId}/attachments/${name}/is-compressed`,
    );
  }

  // ORTHANC API | GET /instances/{id}/attachments/{name}/md5 | Get MD5 of attachment
  async getInstanceAttachmentMD5(
    instanceId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(`/instances/${instanceId}/attachments/${name}/md5`);
  }

  // ORTHANC API | GET /instances/{id}/attachments/{name}/size | Get size of attachment
  async getInstanceAttachmentSize(
    instanceId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(`/instances/${instanceId}/attachments/${name}/size`);
  }

  // ORTHANC API | POST /instances/{id}/attachments/{name}/uncompress | Uncompress attachment
  async uncompressInstanceAttachment(
    instanceId: string,
    name: string,
  ): Promise<any> {
    return this.fetch(
      `/instances/${instanceId}/attachments/${name}/uncompress`,
      { method: 'POST' },
    );
  }

  // ORTHANC API | POST /instances/{id}/attachments/{name}/verify-md5 | Verify attachment
  async verifyInstanceAttachmentMD5(
    instanceId: string,
    name: string,
  ): Promise<any> {
    return this.fetch(
      `/instances/${instanceId}/attachments/${name}/verify-md5`,
      { method: 'POST' },
    );
  }

  // ORTHANC API | GET /instances/{id}/content/{path} | Get raw tag
  // @param tagPath - DICOM tag path, e.g. "0008-0018" or nested "0008-1115/0/0008-0018"
  async getInstanceContent(
    instanceId: string,
    tagPath: string,
  ): Promise<string | null> {
    return this.fetchText(`/instances/${instanceId}/content/${tagPath}`);
  }

  // ORTHANC API | GET /instances/{id}/content/{path} | Get raw tag
  // @param tagPath - DICOM tag path, e.g. "0008-0018" or nested "0008-1115/0/0008-0018"
  async getInstanceContentBinary(
    instanceId: string,
    tagPath: string,
    compress: boolean = false,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/instances/${instanceId}/content/${tagPath}`,
      compress ? { accept: 'application/gzip' } : undefined,
    );
  }

  // ORTHANC API | POST /instances/{id}/export | Write DICOM onto filesystem
  // @param targetPath - Server-side filesystem path to write the file to
  async exportInstance(instanceId: string, targetPath: string): Promise<any> {
    return this.fetch(`/instances/${instanceId}/export`, {
      method: 'POST',
      body: targetPath,
    });
  }

  // ORTHANC API | GET /instances/{id}/file | Download DICOM
  // @returns Raw DICOM file as ArrayBuffer
  async getInstanceFile(
    instanceId: string,
    options?: OrthancFileDownloadOptions,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/instances/${instanceId}/file${OrthancAPIClient.buildQs({
        'filename': options?.filename,
        'transcode': options?.transcode,
        'lossy-quality': options?.lossyQuality,
      })}`,
    );
  }

  // ORTHANC API | GET /instances/{id}/frames | List available frames
  // @returns Array of frame indices [0, 1, 2, ...]
  async getInstanceFrames(instanceId: string): Promise<number[] | null> {
    return this.fetch<number[]>(`/instances/${instanceId}/frames`);
  }

  // ORTHANC API | GET /instances/{id}/frames/{frame} | List operations on frame
  // @param frame - Zero-based frame index
  async getInstanceFrame(instanceId: string, frame: number): Promise<any> {
    return this.fetch(`/instances/${instanceId}/frames/${frame}`);
  }

  // ORTHANC API | GET /instances/{id}/frames/{frame}/image-int16 | Decode a frame (int16)
  async getInstanceFrameImageInt16(
    instanceId: string,
    frame: number,
    options?: OrthancImageOptions,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/instances/${instanceId}/frames/${frame}/image-int16${OrthancAPIClient.buildQs(
        {
          quality: options?.quality,
          returnUnsupportedImage: options?.returnUnsupportedImage,
        },
      )}`,
      options?.accept ? { accept: options.accept } : undefined,
    );
  }

  // ORTHANC API | GET /instances/{id}/frames/{frame}/image-uint16 | Decode a frame (uint16)
  async getInstanceFrameImageUint16(
    instanceId: string,
    frame: number,
    options?: OrthancImageOptions,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/instances/${instanceId}/frames/${frame}/image-uint16${OrthancAPIClient.buildQs(
        {
          quality: options?.quality,
          returnUnsupportedImage: options?.returnUnsupportedImage,
        },
      )}`,
      options?.accept ? { accept: options.accept } : undefined,
    );
  }

  // ORTHANC API | GET /instances/{id}/frames/{frame}/image-uint8 | Decode a frame (uint8)
  async getInstanceFrameImageUint8(
    instanceId: string,
    frame: number,
    options?: OrthancImageOptions,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/instances/${instanceId}/frames/${frame}/image-uint8${OrthancAPIClient.buildQs(
        {
          quality: options?.quality,
          returnUnsupportedImage: options?.returnUnsupportedImage,
        },
      )}`,
      options?.accept ? { accept: options.accept } : undefined,
    );
  }

  // ORTHANC API | GET /instances/{id}/frames/{frame}/matlab | Decode frame for Matlab
  async getInstanceFrameMatlab(
    instanceId: string,
    frame: number,
  ): Promise<string | null> {
    return this.fetchText(`/instances/${instanceId}/frames/${frame}/matlab`);
  }

  // ORTHANC API | GET /instances/{id}/frames/{frame}/numpy | Decode frame for numpy
  async getInstanceFrameNumpy(
    instanceId: string,
    frame: number,
    options?: OrthancNumpyOptions,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/instances/${instanceId}/frames/${frame}/numpy${OrthancAPIClient.buildQs(
        {
          compress: options?.compress,
          rescale: options?.rescale,
        },
      )}`,
    );
  }

  // ORTHANC API | GET /instances/{id}/frames/{frame}/preview | Decode a frame (preview)
  // @param options - quality (JPEG quality 1-100)
  async getInstanceFramePreview(
    instanceId: string,
    frame: number,
    options?: OrthancImageOptions,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/instances/${instanceId}/frames/${frame}/preview${OrthancAPIClient.buildQs(
        {
          quality: options?.quality,
          returnUnsupportedImage: options?.returnUnsupportedImage,
        },
      )}`,
      options?.accept ? { accept: options.accept } : undefined,
    );
  }

  // ORTHANC API | GET /instances/{id}/frames/{frame}/raw | Access raw frame
  async getInstanceFrameRaw(
    instanceId: string,
    frame: number,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(`/instances/${instanceId}/frames/${frame}/raw`);
  }

  // ORTHANC API | GET /instances/{id}/frames/{frame}/raw.gz | Access raw frame (compressed)
  async getInstanceFrameRawGz(
    instanceId: string,
    frame: number,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(`/instances/${instanceId}/frames/${frame}/raw.gz`);
  }

  // ORTHANC API | GET /instances/{id}/frames/{frame}/rendered | Render a frame
  // @param options - quality, width, height, smooth, windowCenter, windowWidth
  async getInstanceFrameRendered(
    instanceId: string,
    frame: number,
    options?: OrthancRenderedOptions,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/instances/${instanceId}/frames/${frame}/rendered${OrthancAPIClient.buildQs(
        {
          'quality': options?.quality,
          'width': options?.width,
          'height': options?.height,
          'smooth': options?.smooth,
          'window-center': options?.windowCenter,
          'window-width': options?.windowWidth,
          'returnUnsupportedImage': options?.returnUnsupportedImage,
        },
      )}`,
      options?.accept ? { accept: options.accept } : undefined,
    );
  }

  // ORTHANC API | GET /instances/{id}/header | Get DICOM meta-header
  // @param options - short/full for tag format
  async getInstanceHeader<T extends OrthancTagFormatOptions | undefined>(
    instanceId: string,
    options?: T,
  ) {
    return this.fetch<TagFormatResult<T>>(
      `/instances/${instanceId}/header${OrthancAPIClient.buildQs({
        short: options?.short,
        simplify: options?.simplify,
      })}`,
    );
  }

  // ORTHANC API | GET /instances/{id}/image-int16 | Decode an image (int16)
  async getInstanceImageInt16(
    instanceId: string,
    options?: OrthancImageOptions,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/instances/${instanceId}/image-int16${OrthancAPIClient.buildQs({
        quality: options?.quality,
        returnUnsupportedImage: options?.returnUnsupportedImage,
      })}`,
      options?.accept ? { accept: options.accept } : undefined,
    );
  }

  // ORTHANC API | GET /instances/{id}/image-uint16 | Decode an image (uint16)
  async getInstanceImageUint16(
    instanceId: string,
    options?: OrthancImageOptions,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/instances/${instanceId}/image-uint16${OrthancAPIClient.buildQs({
        quality: options?.quality,
        returnUnsupportedImage: options?.returnUnsupportedImage,
      })}`,
      options?.accept ? { accept: options.accept } : undefined,
    );
  }

  // ORTHANC API | GET /instances/{id}/image-uint8 | Decode an image (uint8)
  async getInstanceImageUint8(
    instanceId: string,
    options?: OrthancImageOptions,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/instances/${instanceId}/image-uint8${OrthancAPIClient.buildQs({
        quality: options?.quality,
        returnUnsupportedImage: options?.returnUnsupportedImage,
      })}`,
      options?.accept ? { accept: options.accept } : undefined,
    );
  }

  // ORTHANC API | GET /instances/{id}/labels | List labels
  async getInstanceLabels(instanceId: string): Promise<string[] | null> {
    return this.fetch<string[]>(`/instances/${instanceId}/labels`);
  }

  // ORTHANC API | GET /instances/{id}/labels/{label} | Test label
  async hasInstanceLabel(instanceId: string, label: string): Promise<any> {
    return this.fetch(`/instances/${instanceId}/labels/${label}`);
  }

  // ORTHANC API | PUT /instances/{id}/labels/{label} | Add label
  async setInstanceLabel(instanceId: string, label: string): Promise<any> {
    return this.fetch(`/instances/${instanceId}/labels/${label}`, {
      method: 'PUT',
    });
  }

  // ORTHANC API | DELETE /instances/{id}/labels/{label} | Remove label
  async removeInstanceLabel(instanceId: string, label: string): Promise<any> {
    return this.fetch(`/instances/${instanceId}/labels/${label}`, {
      method: 'DELETE',
    });
  }

  // ORTHANC API | GET /instances/{id}/matlab | Decode frame for Matlab
  async getInstanceMatlab(instanceId: string): Promise<string | null> {
    return this.fetchText(`/instances/${instanceId}/matlab`);
  }

  // ORTHANC API | GET /instances/{id}/metadata | List metadata (key names only)
  async getInstanceMetadata(instanceId: string): Promise<string[] | null> {
    return this.fetch<string[]>(`/instances/${instanceId}/metadata`);
  }

  // ORTHANC API | GET /instances/{id}/metadata?expand | List metadata with values
  async getInstanceMetadataExpanded(
    instanceId: string,
  ): Promise<OrthancMetadata | null> {
    return this.fetch<OrthancMetadata>(
      `/instances/${instanceId}/metadata?expand`,
    );
  }

  // ORTHANC API | GET /instances/{id}/metadata/{name} | Get metadata
  async getInstanceMetadataValue(
    instanceId: string,
    name: string,
  ): Promise<string | null> {
    return this.fetchText(`/instances/${instanceId}/metadata/${name}`);
  }

  // ORTHANC API | PUT /instances/{id}/metadata/{name} | Set metadata
  async setInstanceMetadataValue(
    instanceId: string,
    name: string,
    value: string,
  ): Promise<any> {
    return this.fetchText(`/instances/${instanceId}/metadata/${name}`, {
      method: 'PUT',
      body: value,
    });
  }

  // ORTHANC API | DELETE /instances/{id}/metadata/{name} | Delete metadata
  async deleteInstanceMetadataValue(
    instanceId: string,
    name: string,
  ): Promise<any> {
    return this.fetch(`/instances/${instanceId}/metadata/${name}`, {
      method: 'DELETE',
    });
  }

  // ORTHANC API | POST /instances/{id}/modify | Modify instance
  // @returns New modified DICOM instance (binary)
  async modifyInstance(
    instanceId: string,
    request: OrthancModifyRequest,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(`/instances/${instanceId}/modify`, {
      method: 'POST',
      body: request,
    });
  }

  // ORTHANC API | GET /instances/{id}/module | Get instance module
  async getInstanceModule<T extends OrthancTagFormatOptions | undefined>(
    instanceId: string,
    options?: T,
  ) {
    return this.fetch<TagFormatResult<T>>(
      `/instances/${instanceId}/module${OrthancAPIClient.buildQs({
        short: options?.short,
        simplify: options?.simplify,
      })}`,
    );
  }

  // ORTHANC API | GET /instances/{id}/numpy | Decode instance for numpy
  async getInstanceNumpy(
    instanceId: string,
    options?: OrthancNumpyOptions,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/instances/${instanceId}/numpy${OrthancAPIClient.buildQs({
        compress: options?.compress,
        rescale: options?.rescale,
      })}`,
    );
  }

  // ORTHANC API | GET /instances/{id}/patient | Get parent patient
  async getInstanceParentPatient(
    instanceId: string,
  ): Promise<OrthancPatientInfo | null> {
    return this.fetch<OrthancPatientInfo>(`/instances/${instanceId}/patient`);
  }

  // ORTHANC API | GET /instances/{id}/pdf | Get embedded PDF
  async getInstancePdf(instanceId: string): Promise<ArrayBuffer | null> {
    return this.fetchBinary(`/instances/${instanceId}/pdf`);
  }

  // ORTHANC API | GET /instances/{id}/preview | Decode an image (preview)
  // @param options - quality (JPEG quality 1-100)
  async getInstancePreview(
    instanceId: string,
    options?: OrthancImageOptions,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/instances/${instanceId}/preview${OrthancAPIClient.buildQs({
        quality: options?.quality,
        returnUnsupportedImage: options?.returnUnsupportedImage,
      })}`,
      options?.accept ? { accept: options.accept } : undefined,
    );
  }

  // ORTHANC API | POST /instances/{id}/reconstruct | Reconstruct tags & optionally files of instance
  async reconstructInstance(instanceId: string, body?: any): Promise<any> {
    return this.fetch(`/instances/${instanceId}/reconstruct`, {
      method: 'POST',
      body: body || {},
    });
  }

  // ORTHANC API | GET /instances/{id}/rendered | Render an image
  // @param options - quality, width, height, smooth, windowCenter, windowWidth
  async getInstanceRendered(
    instanceId: string,
    options?: OrthancRenderedOptions,
  ): Promise<ArrayBuffer | null> {
    return this.fetchBinary(
      `/instances/${instanceId}/rendered${OrthancAPIClient.buildQs({
        'quality': options?.quality,
        'width': options?.width,
        'height': options?.height,
        'smooth': options?.smooth,
        'window-center': options?.windowCenter,
        'window-width': options?.windowWidth,
        'returnUnsupportedImage': options?.returnUnsupportedImage,
      })}`,
      options?.accept ? { accept: options.accept } : undefined,
    );
  }

  // ORTHANC API | GET /instances/{id}/series | Get parent series
  async getInstanceParentSeries(
    instanceId: string,
  ): Promise<OrthancSeriesInfo | null> {
    return this.fetch<OrthancSeriesInfo>(`/instances/${instanceId}/series`);
  }

  // ORTHANC API | GET /instances/{id}/simplified-tags | Get human-readable tags
  // @param options - ignoreLength: tags whose values should not be truncated
  async getInstanceSimplifiedTags(
    instanceId: string,
    options?: { ignoreLength?: string },
  ) {
    return this.fetch<Record<string, unknown>>(
      `/instances/${instanceId}/simplified-tags${OrthancAPIClient.buildQs({
        'ignore-length': options?.ignoreLength,
      })}`,
    );
  }

  // ORTHANC API | GET /instances/{id}/statistics | Get instance statistics
  async getInstanceStatistics(instanceId: string) {
    return this.fetch<Record<string, string | number>>(`/instances/${instanceId}/statistics`);
  }

  // ORTHANC API | GET /instances/{id}/study | Get parent study
  async getInstanceParentStudy(
    instanceId: string,
  ): Promise<OrthancStudyInfo | null> {
    return this.fetch<OrthancStudyInfo>(`/instances/${instanceId}/study`);
  }

  // ORTHANC API | GET /instances/{id}/tags | Get DICOM tags
  // @param options - short/simplify for tag format, ignoreLength for value truncation
  async getInstanceTags<T extends OrthancTagFormatOptions | undefined>(
    instanceId: string,
    options?: T,
  ) {
    return this.fetch<TagFormatResult<T>>(
      `/instances/${instanceId}/tags${OrthancAPIClient.buildQs({
        'short': options?.short,
        'simplify': options?.simplify,
        'ignore-length': options?.ignoreLength,
      })}`,
    );
  }

  // ================================================================
  //  JOBS
  // ================================================================

  // ORTHANC API | GET /jobs | List jobs
  // @param expand - If true, return full job objects instead of just IDs
  async listJobs(expand: undefined | false): Promise<string[]>;
  async listJobs(expand: true): Promise<OrthancJobInfo[]>;
  async listJobs(expand?: boolean) {
    return this.fetch<string[] | OrthancJobInfo[]>(`/jobs${expand ? '?expand' : ''}`);
  }

  // ORTHANC API | GET /jobs/{id} | Get job
  async getJob(jobId: string): Promise<OrthancJobInfo | null> {
    return this.fetch<OrthancJobInfo>(`/jobs/${jobId}`);
  }

  // ORTHANC API | DELETE /jobs/{id} | Delete job
  async deleteJob(jobId: string): Promise<any> {
    return this.fetch(`/jobs/${jobId}`, { method: 'DELETE' });
  }

  // ORTHANC API | POST /jobs/{id}/cancel | Cancel job
  async cancelJob(jobId: string): Promise<any> {
    return this.fetch(`/jobs/${jobId}/cancel`, { method: 'POST' });
  }

  // ORTHANC API | POST /jobs/{id}/pause | Pause job
  async pauseJob(jobId: string): Promise<any> {
    return this.fetch(`/jobs/${jobId}/pause`, { method: 'POST' });
  }

  // ORTHANC API | POST /jobs/{id}/resubmit | Resubmit job
  async resubmitJob(jobId: string): Promise<any> {
    return this.fetch(`/jobs/${jobId}/resubmit`, { method: 'POST' });
  }

  // ORTHANC API | POST /jobs/{id}/resume | Resume job
  async resumeJob(jobId: string): Promise<any> {
    return this.fetch(`/jobs/${jobId}/resume`, { method: 'POST' });
  }

  // ORTHANC API | GET /jobs/{id}/{key} | Get job output
  async getJobOutput(jobId: string, key: string): Promise<any> {
    return this.fetch(`/jobs/${jobId}/${key}`);
  }

  // ORTHANC API | DELETE /jobs/{id}/{key} | Delete job output
  async deleteJobOutput(jobId: string, key: string): Promise<any> {
    return this.fetch(`/jobs/${jobId}/${key}`, { method: 'DELETE' });
  }

  // ================================================================
  //  NETWORKING — MODALITIES
  // ================================================================

  // ORTHANC API | GET /modalities | List DICOM modalities
  async listModalities() {
    return this.fetch<string[]>('/modalities');
  }

  // ORTHANC API | GET /modalities/{id} | List operations on modality
  async getModality(modalityId: string): Promise<any> {
    return this.fetch(`/modalities/${modalityId}`);
  }

  // ORTHANC API | PUT /modalities/{id} | Create/update modality
  async setModality(
    modalityId: string,
    config: OrthancModalityConfig,
  ): Promise<any> {
    return this.fetch(`/modalities/${modalityId}`, {
      method: 'PUT',
      body: config,
    });
  }

  // ORTHANC API | DELETE /modalities/{id} | Delete modality
  async deleteModality(modalityId: string): Promise<any> {
    return this.fetch(`/modalities/${modalityId}`, { method: 'DELETE' });
  }

  // ORTHANC API | GET /modalities/{id}/configuration | Get modality configuration
  async getModalityConfiguration(
    modalityId: string,
  ): Promise<OrthancModalityConfig | null> {
    return this.fetch<OrthancModalityConfig>(
      `/modalities/${modalityId}/configuration`,
    );
  }

  // ORTHANC API | POST /modalities/{id}/echo | Trigger C-ECHO SCU
  async echoModality(modalityId: string, body?: any): Promise<any> {
    return this.fetch(`/modalities/${modalityId}/echo`, {
      method: 'POST',
      body: body || {},
    });
  }

  // ORTHANC API | POST /modalities/{id}/find | (deprecated) Hierarchical C-FIND SCU
  async findModality(modalityId: string, body: any): Promise<any> {
    return this.fetch(`/modalities/${modalityId}/find`, {
      method: 'POST',
      body,
    });
  }

  // ORTHANC API | POST /modalities/{id}/find-instance | (deprecated) C-FIND SCU for instances
  async findModalityInstance(modalityId: string, body: any): Promise<any> {
    return this.fetch(`/modalities/${modalityId}/find-instance`, {
      method: 'POST',
      body,
    });
  }

  // ORTHANC API | POST /modalities/{id}/find-patient | (deprecated) C-FIND SCU for patients
  async findModalityPatient(modalityId: string, body: any): Promise<any> {
    return this.fetch(`/modalities/${modalityId}/find-patient`, {
      method: 'POST',
      body,
    });
  }

  // ORTHANC API | POST /modalities/{id}/find-series | (deprecated) C-FIND SCU for series
  async findModalitySeries(modalityId: string, body: any): Promise<any> {
    return this.fetch(`/modalities/${modalityId}/find-series`, {
      method: 'POST',
      body,
    });
  }

  // ORTHANC API | POST /modalities/{id}/find-study | (deprecated) C-FIND SCU for studies
  async findModalityStudy(modalityId: string, body: any): Promise<any> {
    return this.fetch(`/modalities/${modalityId}/find-study`, {
      method: 'POST',
      body,
    });
  }

  // ORTHANC API | POST /modalities/{id}/find-worklist | C-FIND SCU for worklist
  async findModalityWorklist(modalityId: string, body: any): Promise<any> {
    return this.fetch(`/modalities/${modalityId}/find-worklist`, {
      method: 'POST',
      body,
    });
  }

  // ORTHANC API | POST /modalities/{id}/get | Trigger C-GET SCU
  async getFromModality(modalityId: string, body: any): Promise<any> {
    return this.fetch(`/modalities/${modalityId}/get`, {
      method: 'POST',
      body,
    });
  }

  // ORTHANC API | POST /modalities/{id}/move | Trigger C-MOVE SCU
  async moveFromModality(
    modalityId: string,
    body: OrthancMoveModalityRequest,
  ): Promise<any> {
    return this.fetch(`/modalities/${modalityId}/move`, {
      method: 'POST',
      body,
    });
  }

  // ORTHANC API | POST /modalities/{id}/query | Trigger C-FIND SCU
  // @returns Query ID for retrieving answers via /queries/{id}/answers
  async queryModality(
    modalityId: string,
    body: OrthancQueryModalityRequest,
  ): Promise<any> {
    return this.fetch(`/modalities/${modalityId}/query`, {
      method: 'POST',
      body,
    });
  }

  // ORTHANC API | POST /modalities/{id}/storage-commitment | Trigger storage commitment request
  async storageCommitmentModality(modalityId: string, body: any): Promise<any> {
    return this.fetch(`/modalities/${modalityId}/storage-commitment`, {
      method: 'POST',
      body,
    });
  }

  // ORTHANC API | POST /modalities/{id}/store | Trigger C-STORE SCU
  async storeToModality(
    modalityId: string,
    body: OrthancStoreModalityRequest | string[],
  ): Promise<any> {
    return this.fetch(`/modalities/${modalityId}/store`, {
      method: 'POST',
      body,
    });
  }

  // ORTHANC API | POST /modalities/{id}/store-straight | Straight C-STORE SCU
  // @param dicomData - Raw DICOM file to send directly
  async storeStraightToModality(
    modalityId: string,
    dicomData: ArrayBuffer,
  ): Promise<any> {
    return this.upload(
      `/modalities/${modalityId}/store-straight`,
      dicomData,
      'application/dicom',
    );
  }

  // ================================================================
  //  NETWORKING — PEERS
  // ================================================================

  // ORTHANC API | GET /peers | List Orthanc peers
  async listPeers() {
    return this.fetch<string[]>('/peers');
  }

  // ORTHANC API | GET /peers/{id} | List operations on peer
  async getPeer(peerId: string): Promise<any> {
    return this.fetch(`/peers/${peerId}`);
  }

  // ORTHANC API | PUT /peers/{id} | Create/update peer
  async setPeer(peerId: string, config: any): Promise<any> {
    return this.fetch(`/peers/${peerId}`, { method: 'PUT', body: config });
  }

  // ORTHANC API | DELETE /peers/{id} | Delete peer
  async deletePeer(peerId: string): Promise<any> {
    return this.fetch(`/peers/${peerId}`, { method: 'DELETE' });
  }

  // ORTHANC API | GET /peers/{id}/configuration | Get peer configuration
  async getPeerConfiguration(peerId: string): Promise<any> {
    return this.fetch(`/peers/${peerId}/configuration`);
  }

  // ORTHANC API | POST /peers/{id}/store | Send to Orthanc peer
  async storeToPeer(peerId: string, body: any): Promise<any> {
    return this.fetch(`/peers/${peerId}/store`, { method: 'POST', body });
  }

  // ORTHANC API | POST /peers/{id}/store-straight | Straight store to peer
  async storeStraightToPeer(
    peerId: string,
    dicomData: ArrayBuffer,
  ): Promise<any> {
    return this.upload(
      `/peers/${peerId}/store-straight`,
      dicomData,
      'application/dicom',
    );
  }

  // ORTHANC API | GET /peers/{id}/system | Get peer system information
  async getPeerSystem(peerId: string): Promise<any> {
    return this.fetch(`/peers/${peerId}/system`);
  }

  // ================================================================
  //  NETWORKING — QUERIES
  // ================================================================

  // ORTHANC API | GET /queries | List query/retrieve operations
  async listQueries(): Promise<string[] | null> {
    return this.fetch<string[]>('/queries');
  }

  // ORTHANC API | GET /queries/{id} | List operations on a query
  async getQuery(queryId: string): Promise<any> {
    return this.fetch(`/queries/${queryId}`);
  }

  // ORTHANC API | DELETE /queries/{id} | Delete query
  async deleteQuery(queryId: string): Promise<any> {
    return this.fetch(`/queries/${queryId}`, { method: 'DELETE' });
  }

  // ORTHANC API | GET /queries/{id}/answers | List answers to a query
  async getQueryAnswers(queryId: string): Promise<any> {
    return this.fetch(`/queries/${queryId}/answers`);
  }

  // ORTHANC API | GET /queries/{id}/answers/{index} | List operations on an answer
  async getQueryAnswer(queryId: string, index: number): Promise<any> {
    return this.fetch(`/queries/${queryId}/answers/${index}`);
  }

  // ORTHANC API | GET /queries/{id}/answers/{index}/content | Get one answer
  async getQueryAnswerContent(queryId: string, index: number): Promise<any> {
    return this.fetch(`/queries/${queryId}/answers/${index}/content`);
  }

  // ORTHANC API | POST /queries/{id}/answers/{index}/query-instances | Query the child instances of an answer
  async queryAnswerInstances(
    queryId: string,
    index: number,
    body?: any,
  ): Promise<any> {
    return this.fetch(`/queries/${queryId}/answers/${index}/query-instances`, {
      method: 'POST',
      body: body || {},
    });
  }

  // ORTHANC API | POST /queries/{id}/answers/{index}/query-series | Query the child series of an answer
  async queryAnswerSeries(
    queryId: string,
    index: number,
    body?: any,
  ): Promise<any> {
    return this.fetch(`/queries/${queryId}/answers/${index}/query-series`, {
      method: 'POST',
      body: body || {},
    });
  }

  // ORTHANC API | POST /queries/{id}/answers/{index}/query-studies | Query the child studies of an answer
  async queryAnswerStudies(
    queryId: string,
    index: number,
    body?: any,
  ): Promise<any> {
    return this.fetch(`/queries/${queryId}/answers/${index}/query-studies`, {
      method: 'POST',
      body: body || {},
    });
  }

  // ORTHANC API | POST /queries/{id}/answers/{index}/retrieve | Retrieve one answer with C-MOVE or C-GET SCU
  // @param body - Target AET string or { TargetAet: string }
  async retrieveQueryAnswer(
    queryId: string,
    index: number,
    body: any,
  ): Promise<any> {
    return this.fetch(`/queries/${queryId}/answers/${index}/retrieve`, {
      method: 'POST',
      body,
    });
  }

  // ORTHANC API | GET /queries/{id}/level | Get level of original query
  async getQueryLevel(queryId: string): Promise<string | null> {
    return this.fetchText(`/queries/${queryId}/level`);
  }

  // ORTHANC API | GET /queries/{id}/modality | Get modality of original query
  async getQueryModality(queryId: string): Promise<string | null> {
    return this.fetchText(`/queries/${queryId}/modality`);
  }

  // ORTHANC API | GET /queries/{id}/query | Get original query arguments
  async getQueryArguments(queryId: string): Promise<any> {
    return this.fetch(`/queries/${queryId}/query`);
  }

  // ORTHANC API | POST /queries/{id}/retrieve | Retrieve all answers with C-MOVE SCU
  async retrieveAllQueryAnswers(queryId: string, body: any): Promise<any> {
    return this.fetch(`/queries/${queryId}/retrieve`, { method: 'POST', body });
  }

  // ================================================================
  //  NETWORKING — STORAGE COMMITMENT
  // ================================================================

  // ORTHANC API | GET /storage-commitment/{id} | Get storage commitment report
  async getStorageCommitment(transactionId: string): Promise<any> {
    return this.fetch(`/storage-commitment/${transactionId}`);
  }

  // ORTHANC API | POST /storage-commitment/{id}/remove | Remove after storage commitment
  async removeAfterStorageCommitment(transactionId: string): Promise<any> {
    return this.fetch(`/storage-commitment/${transactionId}/remove`, {
      method: 'POST',
    });
  }

  // ================================================================
  //  SYSTEM
  // ================================================================

  // ORTHANC API | GET /system | Get system information
  async getSystemInfo(): Promise<OrthancSystemInfo | null> {
    return this.fetch<OrthancSystemInfo>('/system');
  }

  // ORTHANC API | GET /statistics | Get database statistics
  async getSystemStatistics(): Promise<OrthancStatistics | null> {
    return this.fetch<OrthancStatistics>('/statistics');
  }

  // ORTHANC API | GET /plugins | List plugins
  async listPlugins(): Promise<string[] | null> {
    return this.fetch<string[]>('/plugins');
  }

  // ORTHANC API | GET /plugins/explorer.js | JavaScript extensions to Orthanc Explorer
  async getPluginsExplorerJs(): Promise<string | null> {
    return this.fetchText('/plugins/explorer.js');
  }

  // ORTHANC API | GET /plugins/{id} | Get plugin
  async getPlugin(pluginId: string): Promise<any> {
    return this.fetch(`/plugins/${pluginId}`);
  }

  // ================================================================
  //  TOOLS
  // ================================================================

  // ORTHANC API | GET /tools | List operations
  async listTools(): Promise<string[] | null> {
    return this.fetch<string[]>('/tools');
  }

  // ORTHANC API | GET /tools/accepted-sop-classes | Get accepted SOPClassUID
  async getAcceptedSopClasses() {
    return this.fetch<string[]>('/tools/accepted-sop-classes');
  }

  // ORTHANC API | GET /tools/accepted-transfer-syntaxes | Get accepted transfer syntaxes
  async getAcceptedTransferSyntaxes() {
    return this.fetch<string[]>('/tools/accepted-transfer-syntaxes');
  }

  // ORTHANC API | PUT /tools/accepted-transfer-syntaxes | Set accepted transfer syntaxes
  async setAcceptedTransferSyntaxes(syntaxes: string[]) {
    return this.fetch<string[]>('/tools/accepted-transfer-syntaxes', {
      method: 'PUT',
      body: syntaxes,
    });
  }

  // ORTHANC API | POST /tools/bulk-anonymize | Anonymize a set of resources
  async bulkAnonymize(body: any): Promise<any> {
    return this.fetch('/tools/bulk-anonymize', { method: 'POST', body });
  }

  // ORTHANC API | POST /tools/bulk-content | Describe a set of resources
  async bulkContent(body: any): Promise<any> {
    return this.fetch('/tools/bulk-content', { method: 'POST', body });
  }

  // ORTHANC API | POST /tools/bulk-delete | Delete a set of resources
  async bulkDelete(body: { Resources: string[] }): Promise<any> {
    return this.fetch('/tools/bulk-delete', { method: 'POST', body });
  }

  // ORTHANC API | POST /tools/bulk-modify | Modify a set of resources
  async bulkModify(body: any): Promise<any> {
    return this.fetch('/tools/bulk-modify', { method: 'POST', body });
  }

  // ORTHANC API | POST /tools/count-resources | Count local resources
  async countResources(body?: any): Promise<any> {
    return this.fetch('/tools/count-resources', {
      method: 'POST',
      body: body || {},
    });
  }

  // ORTHANC API | GET /tools/create-archive | Create ZIP archive
  async getCreateArchive(): Promise<ArrayBuffer | null> {
    return this.fetchBinary('/tools/create-archive');
  }

  // ORTHANC API | POST /tools/create-archive | Create ZIP archive (from resource list)
  async createArchive(body: {
    Resources: string[];
  }): Promise<ArrayBuffer | null> {
    return this.fetchBinary('/tools/create-archive', { method: 'POST', body });
  }

  // ORTHANC API | POST /tools/create-dicom | Create one DICOM instance
  async createDicom(body: OrthancCreateDicomRequest): Promise<any> {
    return this.fetch('/tools/create-dicom', { method: 'POST', body });
  }

  // ORTHANC API | GET /tools/create-media | Create DICOMDIR media
  async getCreateMedia(): Promise<ArrayBuffer | null> {
    return this.fetchBinary('/tools/create-media');
  }

  // ORTHANC API | POST /tools/create-media | Create DICOMDIR media (from resource list)
  async createMedia(body: {
    Resources: string[];
  }): Promise<ArrayBuffer | null> {
    return this.fetchBinary('/tools/create-media', { method: 'POST', body });
  }

  // ORTHANC API | GET /tools/create-media-extended | Create DICOMDIR media
  async getCreateMediaExtended(): Promise<ArrayBuffer | null> {
    return this.fetchBinary('/tools/create-media-extended');
  }

  // ORTHANC API | POST /tools/create-media-extended | Create DICOMDIR media (extended)
  async createMediaExtended(body: unknown): Promise<ArrayBuffer | null> {
    return this.fetchBinary('/tools/create-media-extended', {
      method: 'POST',
      body,
    });
  }

  // ORTHANC API | GET /tools/default-encoding | Get default encoding
  async getDefaultEncoding(): Promise<string | null> {
    return this.fetchText('/tools/default-encoding');
  }

  // ORTHANC API | PUT /tools/default-encoding | Set default encoding
  async setDefaultEncoding(encoding: string): Promise<any> {
    return this.fetchText('/tools/default-encoding', {
      method: 'PUT',
      body: encoding,
    });
  }

  // ORTHANC API | GET /tools/dicom-conformance | Get DICOM conformance
  async getDicomConformance(): Promise<string | null> {
    return this.fetchText('/tools/dicom-conformance');
  }

  // ORTHANC API | POST /tools/dicom-echo | Trigger C-ECHO SCU
  async dicomEcho(body: {
    AET: string;
    Host: string;
    Port: number;
  }): Promise<any> {
    return this.fetch('/tools/dicom-echo', { method: 'POST', body });
  }

  // ORTHANC API | POST /tools/execute-script | Execute Lua script
  async executeScript(script: string): Promise<any> {
    return this.fetchText('/tools/execute-script', {
      method: 'PUT',
      body: script,
    });
  }

  // ORTHANC API | POST /tools/find | Look for local resources
  async find(request: OrthancFindRequest): Promise<any> {
    return this.fetch('/tools/find', { method: 'POST', body: request });
  }

  // ORTHANC API | GET /tools/generate-uid | Generate an identifier
  // @param level - "patient", "study", "series", or "instance"
  async generateUid(level: string): Promise<string | null> {
    return this.fetchText(`/tools/generate-uid?level=${level}`);
  }

  // ORTHANC API | POST /tools/invalidate-tags | Invalidate DICOM-as-JSON summaries
  async invalidateTags(): Promise<any> {
    return this.fetch('/tools/invalidate-tags', { method: 'POST' });
  }

  // ORTHANC API | GET /tools/labels | Get all the used labels
  async getAllLabels(): Promise<string[] | null> {
    return this.fetch<string[]>('/tools/labels');
  }

  // ORTHANC API | POST /tools/lookup | Look for DICOM identifiers
  async lookupByUIDs(uids: string[]) {
    return this.fetch<{ Type: string; ID: string }[]>('/tools/lookup', {
      method: 'POST',
      body: uids,
    });
  }

  // ORTHANC API | GET /tools/metrics | Are metrics collected?
  async getMetricsEnabled(): Promise<string | null> {
    return this.fetchText('/tools/metrics');
  }

  // ORTHANC API | PUT /tools/metrics | Enable/disable metrics collection
  async setMetricsEnabled(enabled: '0' | '1'): Promise<any> {
    return this.fetchText('/tools/metrics', { method: 'PUT', body: enabled });
  }

  // ORTHANC API | GET /tools/metrics-prometheus | Get usage metrics
  async getMetricsPrometheus(): Promise<string | null> {
    return this.fetchText('/tools/metrics-prometheus');
  }

  // ORTHANC API | GET /tools/now | Get UTC time
  async getNowUtc(): Promise<string | null> {
    return this.fetchText('/tools/now');
  }

  // ORTHANC API | GET /tools/now-local | Get local time
  async getNowLocal(): Promise<string | null> {
    return this.fetchText('/tools/now-local');
  }

  // ORTHANC API | POST /tools/reconstruct | Reconstruct all the index
  async reconstructAll(body?: any): Promise<any> {
    return this.fetch('/tools/reconstruct', {
      method: 'POST',
      body: body || {},
    });
  }

  // ORTHANC API | POST /tools/reset | Restart Orthanc
  async resetOrthanc(): Promise<any> {
    return this.fetch('/tools/reset', { method: 'POST' });
  }

  // ORTHANC API | POST /tools/shutdown | Shutdown Orthanc
  async shutdownOrthanc(): Promise<any> {
    return this.fetch('/tools/shutdown', { method: 'POST' });
  }

  // ORTHANC API | GET /tools/unknown-sop-class-accepted | Is unknown SOP class accepted?
  async isUnknownSopClassAccepted(): Promise<string | null> {
    return this.fetchText('/tools/unknown-sop-class-accepted');
  }

  // ORTHANC API | PUT /tools/unknown-sop-class-accepted | Set unknown SOP class acceptance
  async setUnknownSopClassAccepted(accepted: '0' | '1'): Promise<any> {
    return this.fetchText('/tools/unknown-sop-class-accepted', {
      method: 'PUT',
      body: accepted,
    });
  }

  // ================================================================
  //  LOGS
  // ================================================================

  // ORTHANC API | GET /tools/log-level | Get main log level
  async getLogLevel(): Promise<string | null> {
    return this.fetchText('/tools/log-level');
  }

  // ORTHANC API | PUT /tools/log-level | Set main log level
  // @param level - "default", "verbose", "trace"
  async setLogLevel(level: string): Promise<any> {
    return this.fetchText('/tools/log-level', { method: 'PUT', body: level });
  }

  // ORTHANC API | GET /tools/log-level-dicom | Get log level for dicom
  async getLogLevelDicom(): Promise<string | null> {
    return this.fetchText('/tools/log-level-dicom');
  }

  // ORTHANC API | PUT /tools/log-level-dicom | Set log level for dicom
  async setLogLevelDicom(level: string): Promise<any> {
    return this.fetchText('/tools/log-level-dicom', {
      method: 'PUT',
      body: level,
    });
  }

  // ORTHANC API | GET /tools/log-level-generic | Get log level for generic
  async getLogLevelGeneric(): Promise<string | null> {
    return this.fetchText('/tools/log-level-generic');
  }

  // ORTHANC API | PUT /tools/log-level-generic | Set log level for generic
  async setLogLevelGeneric(level: string): Promise<any> {
    return this.fetchText('/tools/log-level-generic', {
      method: 'PUT',
      body: level,
    });
  }

  // ORTHANC API | GET /tools/log-level-http | Get log level for http
  async getLogLevelHttp(): Promise<string | null> {
    return this.fetchText('/tools/log-level-http');
  }

  // ORTHANC API | PUT /tools/log-level-http | Set log level for http
  async setLogLevelHttp(level: string): Promise<any> {
    return this.fetchText('/tools/log-level-http', {
      method: 'PUT',
      body: level,
    });
  }

  // ORTHANC API | GET /tools/log-level-jobs | Get log level for jobs
  async getLogLevelJobs(): Promise<string | null> {
    return this.fetchText('/tools/log-level-jobs');
  }

  // ORTHANC API | PUT /tools/log-level-jobs | Set log level for jobs
  async setLogLevelJobs(level: string): Promise<any> {
    return this.fetchText('/tools/log-level-jobs', {
      method: 'PUT',
      body: level,
    });
  }

  // ORTHANC API | GET /tools/log-level-lua | Get log level for lua
  async getLogLevelLua(): Promise<string | null> {
    return this.fetchText('/tools/log-level-lua');
  }

  // ORTHANC API | PUT /tools/log-level-lua | Set log level for lua
  async setLogLevelLua(level: string): Promise<any> {
    return this.fetchText('/tools/log-level-lua', {
      method: 'PUT',
      body: level,
    });
  }

  // ORTHANC API | GET /tools/log-level-plugins | Get log level for plugins
  async getLogLevelPlugins(): Promise<string | null> {
    return this.fetchText('/tools/log-level-plugins');
  }

  // ORTHANC API | PUT /tools/log-level-plugins | Set log level for plugins
  async setLogLevelPlugins(level: string): Promise<any> {
    return this.fetchText('/tools/log-level-plugins', {
      method: 'PUT',
      body: level,
    });
  }

  // ORTHANC API | GET /tools/log-level-sqlite | Get log level for sqlite
  async getLogLevelSqlite(): Promise<string | null> {
    return this.fetchText('/tools/log-level-sqlite');
  }

  // ORTHANC API | PUT /tools/log-level-sqlite | Set log level for sqlite
  async setLogLevelSqlite(level: string): Promise<any> {
    return this.fetchText('/tools/log-level-sqlite', {
      method: 'PUT',
      body: level,
    });
  }

  // ================================================================
  //  CONVENIENCE / COMPOSITE QUERIES
  // ================================================================

  // Resolves a DICOM StudyInstanceUID to an Orthanc ID, then returns the study info.
  // Uses POST /tools/lookup internally, falling back to GET /studies?expand client-side filter.
  async findStudyByUID(
    studyInstanceUID: string,
  ): Promise<OrthancStudyInfo | null> {
    const result = await this.lookupByUIDs([studyInstanceUID]);
    if (result?.[0]?.Type === 'Study') {
      return this.getStudy(result[0].ID);
    }
    const studies = await this.listStudiesExpanded();
    return (
      studies?.find(
        s => s.MainDicomTags.StudyInstanceUID === studyInstanceUID,
      ) || null
    );
  }

  // Resolves a DICOM SeriesInstanceUID to an Orthanc ID, then returns series info.
  async findSeriesByUID(
    seriesInstanceUID: string,
  ): Promise<OrthancSeriesInfo | null> {
    const result = await this.lookupByUIDs([seriesInstanceUID]);
    if (result?.[0]?.Type === 'Series') {
      return this.getSeries(result[0].ID);
    }
    return null;
  }

  // Resolves a DICOM SOPInstanceUID to an Orthanc ID, then returns instance info.
  async findInstanceByUID(
    sopInstanceUID: string,
  ): Promise<OrthancInstanceInfo | null> {
    const result = await this.lookupByUIDs([sopInstanceUID]);
    if (result?.[0]?.Type === 'Instance') {
      return this.getInstance(result[0].ID);
    }
    return null;
  }

  // Fetches the full study hierarchy: study → all series → all instances.
  async getFullStudyHierarchy(studyInstanceUID: string): Promise<{
    study: OrthancStudyInfo | null;
    series: OrthancSeriesInfo[];
    instances: OrthancInstanceInfo[];
  }> {
    const study = await this.findStudyByUID(studyInstanceUID);

    if (!study) {
      return { study: null, series: [], instances: [] };
    }

    const [seriesIds, instancesData] = await Promise.all([
      this.getStudySeries(study.ID),
      this.getStudyInstances(study.ID),
    ]);

    const series = seriesIds
      ? await Promise.all(seriesIds.map(async id => this.getSeries(id)))
      : [];

    return {
      study,
      series: series.filter((s): s is OrthancSeriesInfo => s !== null),
      instances: instancesData || [],
    };
  }

  // Fetches instance info + all available metadata in parallel.
  async getInstanceWithFullMetadata(instanceId: string) {
    const [info, metadata, tags, simplifiedTags, attachments, statistics]
      = await Promise.all([
        this.getInstance(instanceId),
        this.getInstanceMetadataExpanded(instanceId),
        this.getInstanceTags(instanceId),
        this.getInstanceSimplifiedTags(instanceId),
        this.getInstanceAttachments(instanceId),
        this.getInstanceStatistics(instanceId),
      ]);

    return { info, metadata, tags, simplifiedTags, attachments, statistics };
  }
}

// ============ Factory Function ============

export function createOrthancClient(
  dicomArchiveConfig: DicomArchiveConfig,
): OrthancAPIClient {
  return new OrthancAPIClient(dicomArchiveConfig);
}

// ============ Convenience Functions ============

export async function getStudyByUID(
  dicomArchiveConfig: DicomArchiveConfig,
  studyInstanceUID: string,
): Promise<OrthancStudyInfo | null> {
  const client = createOrthancClient(dicomArchiveConfig);
  return client.findStudyByUID(studyInstanceUID);
}

export async function getFullStudyData(
  dicomArchiveConfig: DicomArchiveConfig,
  studyInstanceUID: string,
) {
  const client = createOrthancClient(dicomArchiveConfig);
  return client.getFullStudyHierarchy(studyInstanceUID);
}

export async function getInstanceMetadata(
  dicomArchiveConfig: DicomArchiveConfig,
  sopInstanceUID: string,
) {
  const client = createOrthancClient(dicomArchiveConfig);
  const instance = await client.findInstanceByUID(sopInstanceUID);

  if (!instance) return null;

  return client.getInstanceWithFullMetadata(instance.ID);
}

export { OrthancAPIClient };
export default createOrthancClient;
